import React, { useEffect, useState, useMemo, useCallback, useRef, Suspense } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  Stack,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  Drawer,
  TextField,
  MenuItem,
  Menu,
  FormControlLabel,
  Switch,
  Tooltip,
  Snackbar,
  Tabs,
  Tab,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
  GridToolbar,
} from '@mui/x-data-grid';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { filterPublished } from '../../utils/publishedFilter';
import { buildCurriculumTopicOptions } from '../../utils/curriculumTopics';
import { 
  ACADEMIC_YEARS, 
  DIFFICULTIES,
  SUBJECTS, 
  BOARDS, 
  SUBJECT_TOPICS, 
  ACADEMIC_YEAR_BOARD_SUBJECT_TOPICS,
  EXAM_MODES,
  QUESTIONS,
  STATUSES,
  META_TAGS,
  resolveImageDisplayUrl
} from '../../config/Config';
import DetailAnswerDialog from '../../components/forms/DetailAnswerDialog';
import { detailAnswerService } from '../../services/detailAnswerService';
import MathRenderer from '../../components/common/MathRenderer';
import SummaryCard from '../../components/common/SummaryCard';
import QuestionDialog from '../../components/forms/QuestionDialog';
import MultiQuestionEditorDialog from '../../components/forms/MultiQuestionEditorDialog';
import QuestionUploadDialog from '../../components/forms/QuestionUploadDialog';
import QuestionPreviewDialog from '../../components/forms/QuestionPreviewDialog';
import ViewQuestionDialog from '../../components/forms/ViewQuestionDialog';
import AIGenerateDialog from '../../components/forms/AIGenerateDialog';
import AddQuestionWithAIDialog from '../../components/forms/AddQuestionWithAIDialog';
import UpdateQuestionsWithAIDialog from '../../components/forms/UpdateQuestionsWithAIDialog';
import {
  teacherFilterClearButtonSx,
  teacherFilterCompactFieldSx,
  teacherFilterFieldSx,
  teacherFilterHeaderSx,
  teacherFilterPanelSx,
  teacherFilterSectionLabelSx,
  teacherFilterTitleSx,
  teacherFilterToggleSx,
} from '../../styles/teacherFilterStyles';

const QuestionAnalytics = React.lazy(() => import('../../components/analytics/QuestionAnalytics'));

// Difficulty constants removed - now using dynamic difficulties from API

const MAX_UPLOAD_ROWS = 200;
  const DB_FIELDS = ['question_text','board','academic_year','difficulty','question_type','options','correct_answer','answer_evaluation','subject','topic','exam_mode'];



const TYPES = [
  { value: 'MULTIPLE', label: 'Multiple Choice' },
  { value: 'TRUE_FALSE', label: 'True/False' },
  { value: 'SHORT_ANSWER', label: 'Short Answer' }
];
const TYPE_LABELS_MAP = TYPES.reduce((acc,t)=>{acc[t.label.toLowerCase()]=t.value; return acc;},{});

const INITIAL_QUESTION = {
  question_text: '',
  board_years: [{ board: '', academic_year: '' }],
  difficulty: '',
  question_type: 'MULTIPLE',
  is_active: true,
  options: '',
  correct_answer: '',
  subject: '',
  topic: '',
  tags: [],
  has_subquestions: false,
  sub_questions_data: [],
  image_url: ''
};

// Simple button styles – no hover transforms/transitions to avoid performance cost
const bulkActionsButtonStyles = {
  borderRadius: 2,
  textTransform: 'none',
  px: { lg: 2, xl: 3 },
  py: { lg: 0.75, xl: 1 },
  fontWeight: 600,
  borderWidth: 2,
  fontSize: { lg: '0.875rem', xl: '0.9375rem' },
  whiteSpace: 'nowrap',
};

const addQuestionButtonStyles = {
  borderRadius: 2,
  textTransform: 'none',
  px: { lg: 2, xl: 3 },
  py: { lg: 1, xl: 1.5 },
  fontWeight: 600,
  fontSize: { lg: '0.875rem', xl: '0.9375rem' },
  whiteSpace: 'nowrap',
};

const menuItemIconStyles = { mr: 1.5, fontSize: 20 };

/** Isolated dropdown so opening it only re-renders this small component, not the whole QuestionBank. */
function BulkActionsDropdown({ onUploadInstructions, onDownloadTemplate, onUploadQuestions, buttonSx }) {
  const [anchor, setAnchor] = useState(null);
  return (
    <>
      <Button
        variant="outlined"
        endIcon={<ArrowDropDownIcon />}
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={buttonSx}
      >
        Bulk Actions
      </Button>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        transitionDuration={0}
        TransitionProps={{ timeout: 0 }}
        keepMounted
      >
        <MenuItem onClick={() => { onUploadInstructions(); setAnchor(null); }}>
          <WarningAmberIcon sx={menuItemIconStyles} />
          Upload Instructions
        </MenuItem>
        <MenuItem onClick={() => { onDownloadTemplate(); setAnchor(null); }}>
          <DownloadIcon sx={menuItemIconStyles} />
          Download Template
        </MenuItem>
        <MenuItem onClick={() => { onUploadQuestions(); setAnchor(null); }}>
          <UploadFileIcon sx={menuItemIconStyles} />
          Upload Questions
        </MenuItem>
      </Menu>
    </>
  );
}

/** Isolated open state so clicking "Add with AI" only re-renders this component, not the whole QuestionBank. */
function AddWithAIButtonAndDialog({
  subjects,
  allTopics,
  boards,
  academicYears,
  curriculumAssociations,
  difficulties,
  onSuccess,
  onError,
  buttonSx,
  size,
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        variant="outlined"
        color="primary"
        size={size}
        startIcon={<AutoAwesomeIcon />}
        onClick={() => setOpen(true)}
        sx={{ ...buttonSx, borderWidth: 2 }}
      >
        Add with AI
      </Button>
      <AddQuestionWithAIDialog
        open={open}
        onClose={() => setOpen(false)}
        subjects={subjects}
        allTopics={allTopics}
        boards={boards}
        academicYears={academicYears}
        curriculumAssociations={curriculumAssociations}
        difficulties={difficulties}
        onSuccess={(count) => {
          setOpen(false);
          onSuccess(count);
        }}
        onError={onError}
      />
    </>
  );
}

function UpdateWithAIButtonAndDialog({
  subjects,
  allTopics,
  boards,
  academicYears,
  difficulties,
  onSuccess,
  onError,
  buttonSx,
  size,
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        variant="outlined"
        color="secondary"
        size={size}
        startIcon={<AutoAwesomeIcon />}
        onClick={() => setOpen(true)}
        sx={{ ...buttonSx, borderWidth: 2 }}
      >
        Update with AI
      </Button>
      <UpdateQuestionsWithAIDialog
        open={open}
        onClose={() => setOpen(false)}
        subjects={subjects}
        allTopics={allTopics}
        boards={boards}
        academicYears={academicYears}
        difficulties={difficulties}
        onSuccess={(count) => {
          setOpen(false);
          onSuccess(count);
        }}
        onError={onError}
      />
    </>
  );
}

/** Isolated open + form state. Main button opens multi-question editor; single-question dialog kept as backup. */
function AddQuestionButtonAndDialog({
  initialQuestion,
  onSave,
  onSaveMultiple,
  onUpdateRows,
  onDeleteRow,
  onCloseCreateDialog,
  subjects,
  allTopics,
  boards,
  academicYears,
  curriculumAssociations,
  difficulties,
  examModes,
  onOpenDetailAnswer,
  statuses,
  isChampions,
  role,
  detailAnswerRefreshTrigger,
  buttonSx,
  size,
  buttonText = 'Add Question',
}) {
  const [openMulti, setOpenMulti] = useState(false);
  const [openSingle, setOpenSingle] = useState(false);
  const [createForm, setCreateForm] = useState(() => ({ ...initialQuestion }));

  const handleOpen = () => {
    setCreateForm({ ...initialQuestion });
    setOpenMulti(true);
  };

  const handleCloseMulti = () => {
    setOpenMulti(false);
  };

  const handleCloseSingle = () => {
    setOpenSingle(false);
    onCloseCreateDialog?.();
  };

  const handleOpenSingleQuestion = () => {
    setOpenMulti(false);
    setCreateForm({ ...initialQuestion });
    setOpenSingle(true);
  };

  const handleSaveSingle = async (q, img, subImgs) => {
    await onSave(q, img, subImgs);
    setOpenSingle(false);
  };

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        size={size}
        startIcon={<AddIcon />}
        onClick={handleOpen}
        sx={buttonSx}
      >
        {buttonText}
      </Button>
      <MultiQuestionEditorDialog
        open={openMulti}
        onClose={handleCloseMulti}
        onSaveRows={onSaveMultiple}
        onUpdateRows={onUpdateRows}
        onDeleteRow={onDeleteRow}
        onOpenSingleQuestion={handleOpenSingleQuestion}
        onOpenDetailAnswer={onOpenDetailAnswer}
        subjects={subjects}
        allTopics={allTopics}
        boards={boards}
        academicYears={academicYears}
        curriculumAssociations={curriculumAssociations}
        difficulties={difficulties}
        examModes={examModes}
        statuses={statuses}
        isChampions={isChampions}
        role={role}
      />
      <QuestionDialog
        open={openSingle}
        onClose={handleCloseSingle}
        mode="create"
        question={createForm}
        onSave={handleSaveSingle}
        onStateChange={setCreateForm}
        subjects={subjects}
        allTopics={allTopics}
        boards={boards}
        academicYears={academicYears}
        curriculumAssociations={curriculumAssociations}
        difficulties={difficulties}
        examModes={examModes}
        onOpenDetailAnswer={onOpenDetailAnswer}
        statuses={statuses}
        isChampions={isChampions}
        detailAnswerRefreshTrigger={detailAnswerRefreshTrigger}
      />
    </>
  );
}

const QuestionBank = ({
  role = 'teacher',                // 'teacher' | 'champion'
  forceMine = false,               // champions: true (always mine)
  hideSelfOnlyToggle = false,     // champions: true (hide toggle)
  showAllQuestions = false        // admin: true (show all questions)
}) => {
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  // Common styles to reduce duplication
  const commonStyles = {
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 3,
    backgroundColor: 'background.paper',
  };

  // Common DataGrid styles
  const dataGridStyles = {
    minHeight: 400,
    fontSize: { lg: '0.8125rem', xl: '0.875rem' },
    '& .MuiDataGrid-row': {
      minHeight: '60px !important',
      '&:hover': {
        backgroundColor: 'action.hover',
      },
    },
    '& .MuiDataGrid-cell': {
      padding: { lg: '10px 6px', xl: '16px 8px' },
      borderBottom: '1px solid',
      borderColor: 'divider',
    },
    '& .MuiDataGrid-columnHeader': {
      px: { lg: 0.75, xl: 1 },
    },
    '& .MuiDataGrid-columnHeaders': {
      backgroundColor: 'background.paper',
      borderBottom: '2px solid',
      borderColor: 'divider',
    },
  };

  // Common button styles
  const buttonStyles = {
    borderRadius: 2,
    textTransform: 'none',
    py: 1.5,
    fontWeight: 600,
  };

  // list of available Boards (CBSE, ICSE etc.)
  const [boards, setBoards] = useState([]);

  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [totalInDb, setTotalInDb] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [academicYears, setAcademicYears] = useState([]);
  const [examModes, setExamModes] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [topics, setTopics] = useState([]);
  const [allTopics, setAllTopics] = useState([]);
  const [curriculumAssociations, setCurriculumAssociations] = useState([]);
  const [topicCountsById, setTopicCountsById] = useState({});
  const [difficulties, setDifficulties] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [filters, setFilters] = useState({ year: '', board: '', subject: '', topic: '', difficulty: '', type: '', usedIn: '', status: '', hasCorrectAnswer: '', hasDetailAnswer: '', hasImage: '', tags: '' });
  const [availableTags, setAvailableTags] = useState([]);
  
  // Fetch available tags from questions
  useEffect(() => {
    const fetchAvailableTags = async () => {
      try {
        // Fetch all active tags
        const tagsRes = await api.get(`${META_TAGS}?is_active=true&page_size=1000`);
        const allTags = tagsRes.data.results || tagsRes.data || [];
        
        // Get unique tags that are actually used in questions
        const questionsRes = await api.get(`${QUESTIONS}?page_size=1000`);
        const questions = questionsRes.data.results || questionsRes.data || [];
        const usedTagIds = new Set();
        questions.forEach(q => {
          if (q.tags_detail && Array.isArray(q.tags_detail)) {
            q.tags_detail.forEach(tag => usedTagIds.add(tag.id));
          }
        });
        
        // Filter to only tags that are used in questions
        const tagsInUse = allTags.filter(tag => usedTagIds.has(tag.id));
        setAvailableTags(tagsInUse);
      } catch (err) {
        console.error('Failed to fetch available tags', err);
        // Fallback: fetch all active tags
        try {
          const tagsRes = await api.get(`${META_TAGS}?is_active=true&page_size=1000`);
          const allTags = tagsRes.data.results || tagsRes.data || [];
          setAvailableTags(allTags);
        } catch (fallbackErr) {
          console.error('Failed to fetch tags fallback', fallbackErr);
        }
      }
    };
    
    fetchAvailableTags();
  }, []);
  
  // Filter statuses for champions (exclude "Published" from dialogs but keep in filter)
  const dialogStatuses = useMemo(() => {
    if (role !== 'champion') return statuses;
    const filtered = statuses.filter(status => status.value !== 'Published');
    return filtered;
  }, [statuses, role]);
  const [selfOnly, setSelfOnly] = useState(!showAllQuestions);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const hasInitialFetch = useRef(false);
  const hasFetchedOnce = useRef(false);
  const fetchAbortRef = useRef(null);
  const skipNextFilterFetchRef = useRef(false);
  const analyticsAbortRef = useRef(null);
  const topicCountsAbortRef = useRef(null);

  const [analyticsQuestions, setAnalyticsQuestions] = useState([]);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  const fetchPaginatedList = useCallback(async (url) => {
    let nextUrl = url;
    let all = [];
    while (nextUrl) {
      const response = await api.get(nextUrl);
      const data = response.data;
      if (Array.isArray(data)) {
        return data;
      }
      all = all.concat(data.results || []);
      nextUrl = data.next || null;
    }
    return all;
  }, []);

  // Helper function to reset edit question state
  const resetEditQuestion = useCallback(() => {
    setEditQuestion({ ...INITIAL_QUESTION });
    setNewImage(null);
  }, []);

  // Common table styles
  const tableStyles = {
    border: '1px solid #ccc',
    padding: '4px',
  };

  // Common error messages
  const errorMessages = {
    deleteFailed: 'Failed to delete question. Please try again.',
    imageUploadFailed: 'Question saved but image upload failed',
  };

  const sanitizeApiErrorString = (value) => {
    if (value == null) return '';
    const text = String(value).replace(/\s+/g, ' ').trim();
    if (!text) return '';

    const duplicateKeyMatch = text.match(/duplicate key value violates unique constraint.*?DETAIL:\s*Key .*? already exists\./i);
    if (duplicateKeyMatch) {
      if (/uq_exams_question_tenant_hash_gen/i.test(duplicateKeyMatch[0])) {
        return 'This question already exists in your question bank.';
      }
      return 'A duplicate record already exists.';
    }

    const integrityMatch = text.match(/IntegrityError.*?(?=(Request Method:|POST Request URL:|Traceback|During handling of the above exception|$))/i);
    if (integrityMatch) {
      return integrityMatch[0].trim();
    }

    const requestMethodIndex = text.search(/Request Method:|POST Request URL:|Traceback|During handling of the above exception/i);
    const trimmed = requestMethodIndex >= 0 ? text.slice(0, requestMethodIndex).trim() : text;
    return trimmed || text;
  };

  /** Flatten API error response (object/array) to a single readable string for display. */
  const flattenApiError = (data) => {
    if (data == null) return '';
    if (typeof data === 'string') return sanitizeApiErrorString(data);
    if (Array.isArray(data)) {
      return data
        .map((item, i) => {
          const s = typeof item === 'object' && item !== null ? flattenApiError(item) : sanitizeApiErrorString(item);
          return s ? (data.length > 1 ? `[${i}]: ${s}` : s) : '';
        })
        .filter(Boolean)
        .join('; ');
    }
    if (typeof data === 'object') {
      return Object.entries(data)
        .map(([key, val]) => {
          const part = Array.isArray(val)
            ? val.map((item, i) => (typeof item === 'object' && item !== null ? flattenApiError(item) : sanitizeApiErrorString(item)))
              .filter(Boolean)
              .join(', ')
            : (typeof val === 'object' && val !== null ? flattenApiError(val) : sanitizeApiErrorString(val));
          return part ? `${key}: ${part}` : '';
        })
        .filter(Boolean)
        .join('. ');
    }
    return sanitizeApiErrorString(data);
  };



  // Common loading box style
  const loadingBoxStyle = {
    display: 'flex',
    justifyContent: 'center',
    py: 8,
  };

  // Common toolbar box style (flexWrap for mobile)
  const toolbarBoxStyle = {
    p: 1,
    display: 'flex',
    gap: 1,
    alignItems: 'center',
    flexWrap: 'wrap',
  };

  // Helper: parse options string or JSON to array for render/validation
  const parseOptions = (opt) => {
    if (Array.isArray(opt)) return opt;
    if (typeof opt !== 'string') return [];
    const trimmed = opt.trim();
    if (!trimmed) return [];
    try {
      return JSON.parse(trimmed);
    } catch {
      return trimmed.split(',').map(s => s.trim()).filter(Boolean);
    }
  };

  // Dialog state
  const [openUpload, setOpenUpload] = useState(false);
  // Upload state moved to QuestionUploadDialog
  const [addingQuestion, setAddingQuestion] = useState(false);
  // Messages for upload handled inside QuestionUploadDialog
  const [newImage, setNewImage] = useState(null);
  // Submit-valid handled inside QuestionUploadDialog

  // Detail Answer dialog state
  const [openDetailAnswer, setOpenDetailAnswer] = useState(false);
  const [detailAnswerLoading, setDetailAnswerLoading] = useState(false); // true while fetching existing answer (dialog opens immediately)
  const [detailAnswerRefreshTrigger, setDetailAnswerRefreshTrigger] = useState(0);
  const [currentQuestionId, setCurrentQuestionId] = useState(null);
  const [currentDetailAnswer, setCurrentDetailAnswer] = useState(null);
  const [tempDetailAnswerData, setTempDetailAnswerData] = useState(null); // Store temp data for new questions
  const [tempDetailByRowKey, setTempDetailByRowKey] = useState({}); // multi-row-id -> detail answer data
  // instruction dialog
  const [openInstr, setOpenInstr] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [editQuestion, setEditQuestion] = useState({ ...INITIAL_QUESTION });

  // Preview dialog state
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewQuestion, setPreviewQuestion] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewQuestion, setViewQuestion] = useState(null);

  // AI generation state
  const [aiGenerateOpen, setAiGenerateOpen] = useState(false);
  const [aiSourceQuestion, setAiSourceQuestion] = useState(null);






  // Fetch static data only once on mount (boards, subjects, years, etc.)
  useEffect(() => {
    const fetchStaticData = async () => {
      try {
        const [yrsRes, subjRes, boardsRes, topicsRes, modesRes, difficultiesRes, statusesRes, associationsRes] = await Promise.all([
          api.get(`${ACADEMIC_YEARS}?page_size=100&published_only=true`),
          api.get(`${SUBJECTS}?page_size=100`),
          api.get(`${BOARDS}?page_size=100&published_only=true`),
          api.get(`${SUBJECT_TOPICS}?page_size=1000`),
          api.get(`${EXAM_MODES}?page_size=100`),
          api.get(`${DIFFICULTIES}?page_size=100`),
          api.get(`${STATUSES}?page_size=100`),
          fetchPaginatedList(`${ACADEMIC_YEAR_BOARD_SUBJECT_TOPICS}?page_size=10000&is_active=true`)
        ]);

        if (hasFetchedOnce.current) {
          skipNextFilterFetchRef.current = true;
        }
        
        setAcademicYears(filterPublished(yrsRes.data.results || yrsRes.data || []));
        const subs = Array.isArray(subjRes.data) ? subjRes.data : subjRes.data?.results ?? [];
        setSubjects(subs);
        setBoards(filterPublished(boardsRes.data.results || boardsRes.data || []));
        const allT = topicsRes.data.results || topicsRes.data;
        setAllTopics(allT);
        setTopics(allT);
        setExamModes(modesRes.data.results || modesRes.data || []);
        setDifficulties(difficultiesRes.data.results || difficultiesRes.data || []);
        setCurriculumAssociations(associationsRes || []);
        
        // Process statuses
        const statusesData = statusesRes.data.results || statusesRes.data || [];
        if (Array.isArray(statusesData)) {
          const activeStatuses = statusesData
            .filter(status => status.is_active)
            .filter((status, index, self) => 
              index === self.findIndex(s => s.value === status.value)
            );
          setStatuses(activeStatuses);
        }
        
        // Static filter data is loaded independently from the initial question list.
        hasInitialFetch.current = true;
      } catch (err) {
        console.error('Failed fetching static data', err);
        // Keep filters usable even if one lookup endpoint fails.
        hasInitialFetch.current = true;
      }
    };
    
    fetchStaticData();
  }, [fetchPaginatedList]); // Only run once on mount

  // Fetch total count in DB (no filters) for header. Called on mount and when mine changes.
  const fetchTotalInDb = useCallback(async () => {
    const mineFlag = showAllQuestions ? false : (forceMine ? true : selfOnly);
    try {
      const res = await api.get(`/exams/questions/?only_count=1&mine=${mineFlag ? 'true' : 'false'}`);
      setTotalInDb(res.data.total_in_db ?? 0);
    } catch (err) {
      console.error('Failed fetching total question count', err);
    }
  }, [selfOnly, forceMine, showAllQuestions]);

  // Fetch questions (one page; pageSize from paginationModel or override). Cancels in-flight request when filters/page change.
  const fetchQuestions = useCallback(async (page = 1, pageSizeOverride = null) => {
    if (fetchAbortRef.current) {
      fetchAbortRef.current.abort();
    }
    fetchAbortRef.current = new AbortController();
    const signal = fetchAbortRef.current.signal;
    try {
      setLoading(true);
      let examModeParam = null;
      if (filters.usedIn !== '' && filters.usedIn != null) {
        const selected = (examModes || []).find(m => String(m.id) === String(filters.usedIn));
        const isPracticeById = selected && String(selected.mode || '').trim().toLowerCase() === 'practice';
        const isPracticeValue = String(filters.usedIn).toLowerCase() === 'practice';
        if (isPracticeById || isPracticeValue) {
          examModeParam = 'practice';
        } else {
          examModeParam = String(filters.usedIn);
        }
      }

      const mineFlag = showAllQuestions ? false : (forceMine ? true : selfOnly);
      const pageSize = Math.min(100, Math.max(1, Number(pageSizeOverride ?? paginationModel.pageSize) || 25));
      let questionsUrl = `/exams/questions/?mine=${mineFlag ? 'true' : 'false'}&page=${page}&page_size=${pageSize}&list=1${examModeParam ? `&exam_mode=${encodeURIComponent(examModeParam)}` : ''}`;

      if (filters.hasDetailAnswer) questionsUrl += `&has_detail_answer=${filters.hasDetailAnswer}`;
      if (filters.hasCorrectAnswer) questionsUrl += `&has_correct_answer=${filters.hasCorrectAnswer}`;
      if (filters.hasImage) questionsUrl += `&has_image=${filters.hasImage}`;
      if (filters.difficulty) questionsUrl += `&difficulty=${filters.difficulty}`;
      if (filters.year) questionsUrl += `&academic_year=${filters.year}`;
      if (filters.tags) questionsUrl += `&tags=${filters.tags}`;
      if (filters.aiGenerated) questionsUrl += `&is_ai_generated=${filters.aiGenerated}`;
      if (filters.subject) questionsUrl += `&subject=${filters.subject}`;
      if (filters.topic) questionsUrl += `&topic=${filters.topic}`;
      if (filters.board) questionsUrl += `&board=${filters.board}`;
      if (filters.type) questionsUrl += `&type=${encodeURIComponent(filters.type)}`;
      if (filters.status) questionsUrl += `&status=${filters.status}`;

      const qRes = await api.get(questionsUrl, { signal });
      const rawQuestions = qRes.data.results || qRes.data;
      const total = qRes.data.total_count ?? rawQuestions.length;

      const fetchedQs = rawQuestions.map(q => {
        const questionText = q.question_text;
        if (!questionText || typeof questionText !== 'string') {
          console.warn(`Question ${q.id} has invalid question_text:`, questionText);
        }
        return {
          ...q,
          question_text: questionText || '',
          board_years: (q.academic_years || []).map(ay => ({ board: ay.board_id, academic_year: ay.id, difficulty: ay.difficulty_id })),
          tags: (q.tags_detail || []).map(tag => tag.id)
        };
      });

      setQuestions(fetchedQs);
      setTotalCount(total);
    } catch (err) {
      if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return;
      console.error('Failed fetching questions', err);
    } finally {
      setLoading(false);
    }
  }, [role, forceMine, selfOnly, filters.usedIn, filters.hasDetailAnswer, filters.hasCorrectAnswer, filters.hasImage, filters.difficulty, filters.year, filters.tags, filters.aiGenerated, filters.subject, filters.topic, filters.board, filters.type, filters.status, examModes, showAllQuestions, paginationModel.pageSize]);

  // Load the first page immediately. Static filter lookups can be slower than the
  // question list, so do not block the table on boards/subjects/topics metadata.
  useEffect(() => {
    if (hasFetchedOnce.current) return;
    hasInitialFetch.current = true;
    hasFetchedOnce.current = true;
    skipNextFilterFetchRef.current = true;
    fetchTotalInDb();
    fetchQuestions(1);
  }, [fetchQuestions, fetchTotalInDb]);

  // Fetch all questions matching current filters for Analytics tab (independent of Questions tab pagination)
  const fetchAnalyticsQuestions = useCallback(async () => {
    if (analyticsAbortRef.current) analyticsAbortRef.current.abort();
    analyticsAbortRef.current = new AbortController();
    const signal = analyticsAbortRef.current.signal;
    const pageSize = 200;
    let examModeParam = null;
    if (filters.usedIn !== '' && filters.usedIn != null) {
      const selected = (examModes || []).find(m => String(m.id) === String(filters.usedIn));
      const isPracticeById = selected && String(selected.mode || '').trim().toLowerCase() === 'practice';
      const isPracticeValue = String(filters.usedIn).toLowerCase() === 'practice';
      if (isPracticeById || isPracticeValue) examModeParam = 'practice';
      else examModeParam = String(filters.usedIn);
    }
    const mineFlag = showAllQuestions ? false : (forceMine ? true : selfOnly);
    const buildUrl = (page) => {
      let url = `/exams/questions/?mine=${mineFlag ? 'true' : 'false'}&page=${page}&page_size=${pageSize}&list=1${examModeParam ? `&exam_mode=${encodeURIComponent(examModeParam)}` : ''}`;
      if (filters.hasDetailAnswer) url += `&has_detail_answer=${filters.hasDetailAnswer}`;
      if (filters.hasCorrectAnswer) url += `&has_correct_answer=${filters.hasCorrectAnswer}`;
      if (filters.hasImage) url += `&has_image=${filters.hasImage}`;
      if (filters.difficulty) url += `&difficulty=${filters.difficulty}`;
      if (filters.year) url += `&academic_year=${filters.year}`;
      if (filters.tags) url += `&tags=${filters.tags}`;
      if (filters.aiGenerated) url += `&is_ai_generated=${filters.aiGenerated}`;
      if (filters.subject) url += `&subject=${filters.subject}`;
      if (filters.topic) url += `&topic=${filters.topic}`;
      if (filters.board) url += `&board=${filters.board}`;
      if (filters.type) url += `&type=${encodeURIComponent(filters.type)}`;
      if (filters.status) url += `&status=${filters.status}`;
      return url;
    };
    const normalize = (rawList) =>
      (rawList || []).map(q => ({
        ...q,
        question_text: typeof q.question_text === 'string' ? q.question_text : (q.question_text || ''),
        board_years: (q.academic_years || []).map(ay => ({ board: ay.board_id, academic_year: ay.id, difficulty: ay.difficulty_id })),
        tags: (q.tags_detail || []).map(tag => tag.id)
      }));
    try {
      setAnalyticsLoading(true);
      const first = await api.get(buildUrl(1), { signal });
      const rawFirst = first.data.results || first.data || [];
      const totalCount = first.data.total_count ?? rawFirst.length;
      let all = normalize(rawFirst);
      let page = 2;
      while (all.length < totalCount && (page - 1) * pageSize < totalCount) {
        const next = await api.get(buildUrl(page), { signal });
        const rawNext = next.data.results || next.data || [];
        all = all.concat(normalize(rawNext));
        page += 1;
      }
      setAnalyticsQuestions(all);
    } catch (err) {
      if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return;
      console.error('Failed fetching analytics questions', err);
      setAnalyticsQuestions([]);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [forceMine, selfOnly, showAllQuestions, filters.usedIn, filters.hasDetailAnswer, filters.hasCorrectAnswer, filters.hasImage, filters.difficulty, filters.year, filters.tags, filters.aiGenerated, filters.subject, filters.topic, filters.board, filters.type, filters.status, examModes]);

  const fetchTopicCounts = useCallback(async () => {
    if (!filters.subject) {
      setTopicCountsById({});
      return;
    }

    if (topicCountsAbortRef.current) {
      topicCountsAbortRef.current.abort();
    }
    setTopicCountsById({});
    topicCountsAbortRef.current = new AbortController();
    const signal = topicCountsAbortRef.current.signal;

    const pageSize = 200;
    let examModeParam = null;
    if (filters.usedIn !== '' && filters.usedIn != null) {
      const selected = (examModes || []).find(m => String(m.id) === String(filters.usedIn));
      const isPracticeById = selected && String(selected.mode || '').trim().toLowerCase() === 'practice';
      const isPracticeValue = String(filters.usedIn).toLowerCase() === 'practice';
      if (isPracticeById || isPracticeValue) examModeParam = 'practice';
      else examModeParam = String(filters.usedIn);
    }

    const mineFlag = showAllQuestions ? false : (forceMine ? true : selfOnly);
    const buildUrl = (page) => {
      let url = `/exams/questions/?mine=${mineFlag ? 'true' : 'false'}&page=${page}&page_size=${pageSize}&list=1${examModeParam ? `&exam_mode=${encodeURIComponent(examModeParam)}` : ''}`;
      if (filters.hasDetailAnswer) url += `&has_detail_answer=${filters.hasDetailAnswer}`;
      if (filters.hasCorrectAnswer) url += `&has_correct_answer=${filters.hasCorrectAnswer}`;
      if (filters.hasImage) url += `&has_image=${filters.hasImage}`;
      if (filters.difficulty) url += `&difficulty=${filters.difficulty}`;
      if (filters.year) url += `&academic_year=${filters.year}`;
      if (filters.tags) url += `&tags=${filters.tags}`;
      if (filters.aiGenerated) url += `&is_ai_generated=${filters.aiGenerated}`;
      if (filters.subject) url += `&subject=${filters.subject}`;
      if (filters.board) url += `&board=${filters.board}`;
      if (filters.type) url += `&type=${encodeURIComponent(filters.type)}`;
      if (filters.status) url += `&status=${filters.status}`;
      return url;
    };

    try {
      const first = await api.get(buildUrl(1), { signal });
      const rawFirst = first.data.results || first.data || [];
      const totalCount = first.data.total_count ?? rawFirst.length;
      let all = [...rawFirst];
      let page = 2;

      while (all.length < totalCount && (page - 1) * pageSize < totalCount) {
        const next = await api.get(buildUrl(page), { signal });
        const rawNext = next.data.results || next.data || [];
        all = all.concat(rawNext);
        page += 1;
      }

      const counts = {};
      all.forEach((question) => {
        const topicValue = question.topic;
        let topicId = null;
        if (topicValue && typeof topicValue === 'object') {
          topicId = topicValue.id;
        } else if (topicValue !== null && topicValue !== undefined && topicValue !== '') {
          topicId = topicValue;
        }
        if (topicId == null) return;
        const normalizedId = String(topicId);
        counts[normalizedId] = (counts[normalizedId] || 0) + 1;
      });

      setTopicCountsById(counts);
    } catch (err) {
      if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return;
      console.error('Failed fetching topic counts', err);
      setTopicCountsById({});
    }
  }, [forceMine, selfOnly, showAllQuestions, filters.usedIn, filters.hasDetailAnswer, filters.hasCorrectAnswer, filters.hasImage, filters.difficulty, filters.year, filters.tags, filters.aiGenerated, filters.subject, filters.board, filters.type, filters.status, examModes]);

  // DataGrid v5 uses onPageChange / onPageSizeChange (not onPaginationModelChange). Trigger fetch when user clicks Next/Prev or changes rows per page.
  const handlePageChange = useCallback((newPage) => {
    setPaginationModel(prev => ({ ...prev, page: newPage }));
    if (hasInitialFetch.current && hasFetchedOnce.current) {
      fetchQuestions(newPage + 1, paginationModel.pageSize);
    }
  }, [fetchQuestions, paginationModel.pageSize]);

  const handlePageSizeChange = useCallback((newPageSize) => {
    setPaginationModel(prev => ({ ...prev, pageSize: newPageSize, page: 0 }));
    if (hasInitialFetch.current && hasFetchedOnce.current) {
      fetchQuestions(1, newPageSize);
    }
  }, [fetchQuestions]);

  // Common DataGrid configuration (server-side pagination; v5 API: page, onPageChange, pageSize, onPageSizeChange)
  const dataGridConfig = {
    paginationMode: 'server',
    pagination: true,
    rowCount: totalCount,
    page: paginationModel.page,
    pageSize: paginationModel.pageSize,
    onPageChange: handlePageChange,
    onPageSizeChange: handlePageSizeChange,
    rowsPerPageOptions: [25, 50, 100],
    disableSelectionOnClick: true,
    autoHeight: true,
    getRowId: (row) => row.id || `temp-${Math.random()}`,
    getRowHeight: () => 'auto',
  };

  // Fetch total in DB for header (on mount and when mine changes)
  useEffect(() => {
    if (!hasInitialFetch.current) return;
    fetchTotalInDb();
  }, [fetchTotalInDb]);

  // Reset to first page only when filter values change (not on every render — avoids bounce)
  useEffect(() => {
    setPaginationModel(prev => (prev.page === 0 ? prev : { ...prev, page: 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.usedIn, filters.hasDetailAnswer, filters.hasCorrectAnswer, filters.hasImage, filters.difficulty, filters.year, filters.tags, filters.aiGenerated, filters.subject, filters.topic, filters.board, filters.type, filters.status, selfOnly, showAllQuestions]);

  // Fetch questions when static data is loaded or when filters change (page changes are handled by handlePaginationModelChange)
  useEffect(() => {
    if (!hasInitialFetch.current) return;

    if (!hasFetchedOnce.current) {
      hasFetchedOnce.current = true;
      skipNextFilterFetchRef.current = true; // Skip the next effect run to avoid double fetch / bounce
      fetchTotalInDb(); // Load header total on first load so "Total Questions" shows number, not "-"
      fetchQuestions(1);
      return;
    }

    if (skipNextFilterFetchRef.current) {
      skipNextFilterFetchRef.current = false;
      return;
    }

    const timer = setTimeout(() => {
      fetchQuestions(1); // Filters changed; reset effect already set page to 0, so fetch page 1
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selfOnly, filters.usedIn, filters.hasDetailAnswer, filters.hasCorrectAnswer, filters.hasImage, filters.difficulty, filters.year, filters.tags, filters.aiGenerated, filters.subject, filters.topic, filters.board, filters.type, filters.status, showAllQuestions, examModes]);

  // Fetch all filtered questions for Analytics when Analytics tab is active; refetch when filters change
  useEffect(() => {
    if (!hasInitialFetch.current || activeTab !== 1) return;
    fetchAnalyticsQuestions();
    return () => {
      if (analyticsAbortRef.current) analyticsAbortRef.current.abort();
    };
  }, [activeTab, fetchAnalyticsQuestions, filters.usedIn, filters.hasDetailAnswer, filters.hasCorrectAnswer, filters.hasImage, filters.difficulty, filters.year, filters.tags, filters.aiGenerated, filters.subject, filters.topic, filters.board, filters.type, filters.status, selfOnly, showAllQuestions]);

  useEffect(() => {
    if (!hasInitialFetch.current) return;
    fetchTopicCounts();
    return () => {
      if (topicCountsAbortRef.current) topicCountsAbortRef.current.abort();
    };
  }, [fetchTopicCounts]);

  // Only offer topics mapped to the selected active curriculum.
  useEffect(() => {
    const curriculumTopics = buildCurriculumTopicOptions({
      rows: curriculumAssociations,
      topics: allTopics,
      board: filters.board,
      academicYear: filters.year,
      subject: filters.subject,
    });
    setTopics(curriculumTopics);
    setFilters((prev) => (prev.topic ? { ...prev, topic: '' } : prev));
  }, [filters.board, filters.year, filters.subject, allTopics, curriculumAssociations]);



  const handleFilterChange = (field) => (e) => {
    setFilters(prev => ({ ...prev, [field]: e.target.value }));
  };

      const clearFilters = () => {
        setFilters({ year: '', board: '', subject: '', topic: '', difficulty: '', type: '', usedIn: '', status: '', hasCorrectAnswer: '', hasDetailAnswer: '', hasImage: '', tags: '', aiGenerated: '' });
    };

  const getCreatorId = (q) => {
    // Handle different possible structures for created_by
    if (q.created_by && typeof q.created_by === 'object' && q.created_by.id) {
      return String(q.created_by.id);
    } else if (q.created_by) {
      return String(q.created_by);
    } else if (q.created_by_id) {
      return String(q.created_by_id);
    }
    return '';
  };
  
  // Current page rows for table and analytics (server returns filtered slice)
  const filteredRows = questions;

  // Upload validation state handled within QuestionUploadDialog

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this question? This will also delete:\n• Question image\n• Detailed answers\n• Detailed answer images\n\nThis action cannot be undone.')) return false;
    try {
      await api.delete(`${QUESTIONS}${id}/`);
      // Refetch current page and total for server-side pagination
      fetchQuestions(paginationModel.page + 1);
      fetchTotalInDb();
      return true;
    } catch (e) {
      console.error('Delete failed', e);
      alert(errorMessages.deleteFailed);
      throw e;
    }
  };





  const columns = [
    { field: 'actions', headerName: 'Actions', width: 120, sortable:false, renderCell: (params) => {
      const canEdit = role === 'admin'
        || (getCreatorId(params.row) === String(user?.id) && params.row.used_in_tests === 0);
      const canDelete = params.row.used_in_tests === 0 && getCreatorId(params.row) === String(user?.id);
      
      const handleMenuOpen = (event) => {
        event.stopPropagation();
        setActionMenuAnchor(event.currentTarget);
        setSelectedRow(params.row);
      };

      const handleMenuClose = () => {
        setActionMenuAnchor(null);
        setSelectedRow(null);
      };

      return (
        <Stack direction="row" spacing={0.5} alignItems="center">
          {/* Preview button - Quick inline icon */}
          <Tooltip title="Preview">
            <IconButton
              size="small"
              color="info"
              onClick={(e) => {
                e.stopPropagation();
                setViewQuestion(params.row);
                setViewDialogOpen(true);
              }}
              aria-label="Preview question"
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          
          {/* Edit button - Quick inline icon */}
          <Tooltip title={canEdit ? 'Edit Question' : (params.row.used_in_tests > 0 ? 'Cannot edit – used in tests' : 'Cannot edit – not owner')}>
            <span>
              <IconButton 
                size="small" 
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenEdit(params.row);
                }} 
                disabled={!canEdit}
                aria-label="Edit question"
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          {/* 3-dot menu button */}
          <IconButton
            size="small"
            onClick={handleMenuOpen}
            aria-label="More actions"
            sx={{ ml: 0.5 }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>

          {/* Context menu */}
          <Menu
            anchorEl={actionMenuAnchor}
            open={Boolean(actionMenuAnchor) && selectedRow?.id === params.row.id}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            onClick={(e) => e.stopPropagation()}
          >
            {role !== 'champion' && (
              <MenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenAIGenerate(params.row);
                  handleMenuClose();
                }}
              >
                <AutoAwesomeIcon sx={{ mr: 1.5, fontSize: 20 }} />
                Generate Similar (AI)
              </MenuItem>
            )}
            
            {/* Admin Preview - Inside menu (Admin only) */}
            {role === 'admin' && (
              <MenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenPreviewDialog(params.row);
                  handleMenuClose();
                }}
              >
                <QuestionMarkIcon sx={{ mr: 1.5, fontSize: 20 }} />
                Preview & Update Status
              </MenuItem>
            )}
            
            {/* Delete - Inside menu */}
            <MenuItem
              onClick={(e) => {
                e.stopPropagation();
                if (canDelete) {
                  handleDelete(params.row.id);
                }
                handleMenuClose();
              }}
              disabled={!canDelete}
            >
              <DeleteOutlineIcon sx={{ mr: 1.5, fontSize: 20, color: canDelete ? 'error.main' : 'text.disabled' }} />
              <Typography sx={{ color: canDelete ? 'error.main' : 'text.disabled' }}>
                Delete
              </Typography>
            </MenuItem>
          </Menu>
        </Stack>
      );
    } },
    { field: 'id', headerName: 'ID', width: 70 },
    { 
      field: 'question_text', 
      headerName: 'Question', 
      flex: 1, 
      minWidth: 320,
      renderCell: (params) => {
        const question = params.row;
        const opts = parseOptions(question.options);
        const correct = String(question.correct_answer ?? '').trim();
        // Find the correct answer index - handle case-insensitive matching
        const firstMatchIdx = opts.findIndex(o => 
          String(o).trim().toLowerCase() === correct.toLowerCase() ||
          String(o).trim() === correct
        );
        
        // Use image_proxy_url (object_name pattern, same as detail answer) so images are served from object storage
        const imageUrl = question.image_proxy_url || question.image_url || null;
        
        return (
          <Box sx={{ width: '100%', py: 1, minHeight: '60px' }}>
            {/* Question Text */}
            <MathRenderer 
              content={question.question_text}
              variant="body2"
              sx={{ mb: 1, fontWeight: 500, lineHeight: 1.4 }}
            />
            
            {/* Question Image */}
            {imageUrl && (
              <Box sx={{ mb: 1 }}>
                <img 
                  src={resolveImageDisplayUrl(imageUrl)} 
                  alt="question" 
                  style={{ 
                    maxWidth: '200px', 
                    maxHeight: '150px', 
                    objectFit: 'contain',
                    borderRadius: '4px'
                  }}
                  onError={(e) => {
                    console.error('Failed to load image for question:', question.id);
                    e.target.style.display = 'none';
                  }}
                />
              </Box>
            )}
            
            {/* Options */}
            {opts.length > 0 && (
              <Stack spacing={0.5} sx={{ mt: 1 }}>
                {opts.map((opt, idx) => {
                  const isCorrect = idx === firstMatchIdx;
                  return (
                    <Box 
                      key={idx} 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        px: 1, 
                        py: 0.5, 
                        borderRadius: 1, 
                        bgcolor: isCorrect ? 'success.light' : 'grey.50',
                        border: isCorrect ? '1px solid' : '1px solid transparent',
                        borderColor: isCorrect ? 'success.main' : 'transparent',
                        minHeight: '32px'
                      }}
                    >
                      <Typography 
                        variant="body2" 
                        fontWeight="bold" 
                        sx={{ mr: 1, color: isCorrect ? 'success.dark' : 'text.primary' }}
                      >
                        {idx + 1}.
                      </Typography>
                      <MathRenderer 
                        content={opt}
                        variant="body2"
                        flexGrow={1}
                        sx={{ color: isCorrect ? 'success.dark' : 'text.primary' }}
                      />
                      {isCorrect && (
                        <CheckCircleOutlineIcon 
                          fontSize="small" 
                          color="success" 
                          sx={{ ml: 1 }} 
                        />
                      )}
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Box>
        );
      }
    },
    { field: 'board', headerName: 'Board', width: 120, valueGetter: (p) => {
        // Check if academic_years array exists and has items
        if (p.row.academic_years && p.row.academic_years.length > 0) {
          // Return comma-separated board names (use board_name instead of board code)
          const boardNames = p.row.academic_years.map(ay => ay.board_name || ay.board || '').filter(Boolean);
          return boardNames.join(', ');
        }
        return '';
      } },
    { field: 'academic_year', headerName: 'Year', width: 100, valueGetter: (p) => {
        // Check if academic_years array exists and has items
        if (p.row.academic_years && p.row.academic_years.length > 0) {
          // Return comma-separated years
          const years = p.row.academic_years.map(ay => ay.year || '').filter(Boolean);
          return years.join(', ');
        }
        return '';
      } },
    { field: 'topic', headerName: 'Topic', width: 130, valueGetter: (p) => {
        // Get topic name from the topic object, topic_name field, or ID
        if (p.row.topic_name) {
          return p.row.topic_name;
        } else if (p.row.topic) {
          if (typeof p.row.topic === 'object' && p.row.topic.topic) {
            return p.row.topic.topic;
          } else if (typeof p.row.topic === 'number' || typeof p.row.topic === 'string') {
            // Try to find topic by ID
            const topicId = typeof p.row.topic === 'string' ? parseInt(p.row.topic, 10) : p.row.topic;
            const topicObj = allTopics.find(t => t.id === topicId);
            return topicObj ? topicObj.topic : p.row.topic;
          }
        }
        return '';
      } },
    { field: 'difficulty', headerName: 'Difficulty', width: 120, valueGetter: (p) => {
        // Check if academic_years array exists and has items
        if (p.row.academic_years && p.row.academic_years.length > 0) {
          // Return comma-separated difficulty names
          const difficulties = p.row.academic_years.map(ay => ay.difficulty || '').filter(Boolean);
          return difficulties.join(', ');
        }
        return '';
      } },
    { field: 'question_type', headerName: 'Type', width: 125 },
    { 
      field: 'is_ai_generated', 
      headerName: 'AI Generated', 
      width: 110,
      renderCell: (params) => {
        return params.value ? (
          <Chip label="Yes" size="small" color="primary" variant="outlined" />
        ) : (
          <Chip label="No" size="small" variant="outlined" />
        );
      }
    },
    { field: 'status_display', headerName: 'Status', width: 120, renderCell: (params) => {
        const status = params.row.status_display || 'Not Set';
        let color = 'default';
        let bgColor = 'grey.100';
        
        if (status === 'Published') {
          color = 'success';
          bgColor = 'success.light';
        } else if (status === 'Rejected') {
          color = 'error';
          bgColor = 'error.light';
        }
        
        return (
          <Chip
            label={status}
            size="small"
            color={color}
            sx={{ 
              backgroundColor: bgColor,
              fontWeight: 'medium'
            }}
          />
        );
      } }
  ];



  /* Upload */
  // Upload flow moved to QuestionUploadDialog

  // Submit valid moved to QuestionUploadDialog

  const validateRow = (obj) => {
    const errs = {};
    // Helper function to find object by ID or name
    const findByIdOrName = (array, value, nameField = 'name') => {
      return array.find(item => item.id === value) || 
             array.find(item => item[nameField].toLowerCase() === String(value).trim().toLowerCase());
    };

    // required fields
    if (!obj.question_text) errs.question_text = 'Required';
    // academic_year should be the year number (e.g., 7, 10)
    if (!obj.board){
       errs.board='Required';
     } else {
       const bOk = findByIdOrName(boards, obj.board);
       if(!bOk) errs.board='Invalid';
     }
     if (!obj.academic_year) {
       errs.academic_year = 'Required';
    } else {
       const ayObj = academicYears.find((y)=> y.year === obj.academic_year || y.id === obj.academic_year);
       if (!ayObj) errs.academic_year = 'Invalid';
    }
    // Difficulty validation removed - now handled at board-year pair level
    if (!Object.values(TYPE_LABELS_MAP).includes(obj.question_type) && !Object.keys(TYPE_LABELS_MAP).includes(String(obj.question_type).toLowerCase())) errs.question_type='Invalid';
    if (!obj.subject) {
       errs.subject = 'Required';
    } else {
       const ok = findByIdOrName(subjects, obj.subject);
       if (!ok) errs.subject='Invalid';
    }
    if (obj.topic){
       const okT = findByIdOrName(allTopics, obj.topic, 'topic');
       if(!okT) errs.topic='Invalid';
    }
    if(obj.options && typeof obj.options==='string'){
       const arr = parseOptions(obj.options);
       if(!arr.length) errs.options='Invalid';
       else obj.options = JSON.stringify(arr);
     }
     // correct answer inclusion check
     if(['MULTIPLE','TRUE_FALSE'].includes(String(obj.question_type).toUpperCase())){
        const optsArr = parseOptions(obj.options);
        if(!optsArr.includes(obj.correct_answer)) errs.correct_answer='Not in options';
     }
    return errs;
  };

  // Upload preview columns are defined inside QuestionUploadDialog

  // Map of subject -> array of topics for instruction table
  const subjectTopicMap = React.useMemo(()=>{
    const map = {};
    allTopics.forEach((t)=>{
      const subObj = subjects.find(s=>String(s.id)===String(t.subject));
      if(subObj){
        if(!map[subObj.name]) map[subObj.name]=[];
        map[subObj.name].push(t.topic);
      }
    });
    // sort for nicer display
    Object.keys(map).forEach(k=> map[k].sort());
    return map;
  }, [allTopics, subjects]);

  



  // Helper function for parsing options (used in upload functionality)
  

  /* Add new question */
  const handleOpenEdit = async (row) => {
    if (!row?.id) {
      setEditQuestion(row);
      setNewImage(null);
      setOpenEdit(true);
      return;
    }
    try {
      const res = await api.get(`${QUESTIONS}${row.id}/`);
      const full = res.data;
      const merged = {
        ...row,
        ...full,
        image_url: full.image_url ?? row.image_url ?? '',
        board_years: (full.academic_years || row.academic_years || []).map(ay => ({
          board: ay.board_id ?? ay.board ?? '',
          academic_year: ay.id ?? ay.academic_year ?? '',
          difficulty: ay.difficulty_id ?? ay.difficulty ?? ''
        })),
        tags: (full.tags_detail || full.tags || []).map(t => (typeof t === 'number' ? t : t.id)),
        sub_questions_data: (full.sub_questions || []).map(sq => ({
          ...sq,
          options: Array.isArray(sq.options) ? sq.options.join(', ') : (sq.options || ''),
          image_url: sq.image_url ?? ''
        }))
      };
      setEditQuestion(merged);
      setNewImage(null);
      setOpenEdit(true);
    } catch (err) {
      console.error('Failed to load question for edit:', err);
      setEditQuestion(row);
      setNewImage(null);
      setOpenEdit(true);
    }
  };

  const handleOpenAIGenerate = (row) => {
    setAiSourceQuestion(row);
    setAiGenerateOpen(true);
  };

  /* Preview and status update functions */
  const handleStatusUpdate = (questionId, statusUpdate) => {
    const statusDisplay = typeof statusUpdate === 'string'
      ? statusUpdate
      : (statusUpdate?.status_display || statusUpdate?.new_status || '');

    // Update the question status in the local state
    setQuestions(prevQuestions => 
      prevQuestions.map(q => 
        q.id === questionId ? {
          ...q,
          status: statusUpdate?.status_id ?? q.status,
          status_display: statusDisplay || q.status_display,
          status_detail: statusUpdate?.status_detail ?? q.status_detail,
        } : q
      )
    );
  };

  const handleOpenPreviewDialog = (question) => {
    setPreviewQuestion(question);
    setPreviewDialogOpen(true);
  };



  // Comprehensive validation function for both create and edit modes
  const validateQuestion = (q) => {
    const errors = [];
    
    // Required field validations
    if (!(typeof q.question_text === 'string' && q.question_text.trim())) {
      errors.push('Question text is required');
    }
    
    // Difficulty validation removed - now handled at board-year pair level
    
    if (!q.question_type) {
      errors.push('Question type is required');
    }
    
    if (!q.subject) {
      errors.push('Subject is required');
    }
    
    if (!q.topic) {
      errors.push('Topic is required');
    }
    
    // Correct answer is optional for the main question.
    if (q.has_subquestions) {
      // When has sub-questions, require at least one sub-question with valid data
      if (!q.sub_questions_data || !Array.isArray(q.sub_questions_data) || q.sub_questions_data.length === 0) {
        errors.push('At least one sub-question is required when "Have sub questions?" is on');
      } else {
        for (let i = 0; i < q.sub_questions_data.length; i++) {
          const sq = q.sub_questions_data[i];
          if (!(typeof sq.question_text === 'string' && sq.question_text.trim())) {
            errors.push(`Sub-question ${i + 1}: question text is required`);
          }
          if (sq.question_type === 'MULTIPLE') {
            const opts = parseOptions(sq.options);
            if (!Array.isArray(opts) || opts.length === 0) {
              errors.push(`Sub-question ${i + 1}: options are required for Multiple Choice`);
            }
          }
        }
      }
    }
    
    // Board-year pair validation
    if (!q.board_years || q.board_years.length === 0) {
      errors.push('At least one Board-Year pair is required');
    } else {
      const invalidPair = q.board_years.some(p => !p.board || !p.academic_year);
      if (invalidPair) {
        errors.push('Each Board-Year pair must have both fields selected');
      }
    }
    
    // Options and correct answer validation for main question (only when no sub-questions)
    if (!q.has_subquestions) {
      if (q.question_type === 'MULTIPLE') {
        const opts = parseOptions(q.options);
        if (!Array.isArray(opts) || opts.length === 0) {
          errors.push('Options must be a non-empty array for Multiple Choice questions');
        } else if (q.correct_answer && !opts.map((o) => String(o).trim()).includes(String(q.correct_answer).trim())) {
          errors.push('Correct answer must be one of the options');
        }
      }
      if (q.question_type === 'TRUE_FALSE') {
        if (q.correct_answer && !['True', 'False'].includes(q.correct_answer)) {
          errors.push('Correct answer must be either "True" or "False"');
        }
      }
    }
    
    // For SHORT_ANSWER questions, options are not required
    // The validation above already handles MULTIPLE and TRUE_FALSE
    
    return {
      isValid: errors.length === 0,
      errors: errors
    };
  };



  // Helper function to prepare options based on question type
  const prepareOptionsForPayload = (question) => {
    // If question has sub-questions, main question doesn't have options
    if (question.has_subquestions) {
      return [];
    }
    if (question.question_type === 'TRUE_FALSE') {
      // For TRUE_FALSE questions, always set options to ["True", "False"]
      return ["True", "False"];
    } else if (question.options) {
      return parseOptions(question.options);
    }
    return [];
  };

  const handleUpdateQuestion = async (processedQuestion = null, imageFile = null, subQuestionImageFiles = {}, subQuestionDetailImageFiles = {}) => {
    // Use processed question from QuestionDialog if provided, otherwise use state
    // imageFile: main-question image; subQuestionImageFiles: { index: File } for sub-question images
    const questionToProcess = processedQuestion || editQuestion;
    
    if (!questionToProcess) return;
    
    // Apply comprehensive validation before updating
    const validation = validateQuestion(questionToProcess);
    if (!validation.isValid) {
      const msg = validation.errors?.length ? validation.errors.join('. ') : 'Please fix the validation errors above.';
      setSnackMessage(msg);
      setSnackOpen(true);
      return;
    }
    
    try {
      // Build PATCH payload with only writable fields to avoid 400 from read-only/extra data
      const boardYears = (questionToProcess.board_years || []).map(p => ({
        board: parseInt(p.board, 10),
        academic_year: parseInt(p.academic_year, 10),
        difficulty: parseInt(p.difficulty, 10),
      })).filter(p => !Number.isNaN(p.board) && !Number.isNaN(p.academic_year) && !Number.isNaN(p.difficulty));
      let tags = (questionToProcess.tags && Array.isArray(questionToProcess.tags))
        ? questionToProcess.tags
            .map(t => (typeof t === 'number' ? t : (t?.id != null ? t.id : null)))
            .filter(id => id != null && typeof id === 'number' && id > 0)
        : [];
      const subQuestionsData = (questionToProcess.has_subquestions && questionToProcess.sub_questions_data)
        ? questionToProcess.sub_questions_data.map((sq, index) => {
            const item = {
              question_text: sq.question_text || '',
              question_type: sq.question_type || 'MULTIPLE',
              options: sq.question_type === 'MULTIPLE' ? parseOptions(sq.options) : [],
              correct_answer: sq.correct_answer || '',
              detail_answer: sq.detail_answer || '',
              answer_evaluation: sq.answer_evaluation || {},
              marks: sq.marks ?? 1,
              order: index + 1,
              is_active: sq.is_active !== undefined ? sq.is_active : true,
            };
            // Only send image_url when it's a stored URL (OCI or media); omit for empty, blob, proxy, or relative
            const url = sq.image_url && typeof sq.image_url === 'string' ? sq.image_url.trim() : '';
            // Exclude proxy URLs (contain '/image-proxy/' or '/image-proxy?') and blob URLs
            if (url &&
                (url.startsWith('http://') || url.startsWith('https://')) &&
                !url.includes('/image-proxy/') &&
                !url.includes('/image-proxy?') &&
                !url.startsWith('blob:')) {
              item.image_url = url;
            }
            // Same rule for the detail answer image (backend recreates sub-questions on update)
            const detailUrl = sq.detail_answer_image && typeof sq.detail_answer_image === 'string' ? sq.detail_answer_image.trim() : '';
            if (detailUrl &&
                (detailUrl.startsWith('http://') || detailUrl.startsWith('https://')) &&
                !detailUrl.includes('/image-proxy/') &&
                !detailUrl.includes('/image-proxy?') &&
                !detailUrl.startsWith('blob:')) {
              item.detail_answer_image = detailUrl;
            }
            // Debug: log if we're including image_url
            if (item.image_url) {
              console.log(`Sub-question ${index} image_url:`, item.image_url);
            }
            return item;
          })
        : [];
      const payload = {
        question_text: questionToProcess.question_text,
        question_type: questionToProcess.question_type,
        subject: questionToProcess.subject,
        topic: questionToProcess.topic,
        has_subquestions: !!questionToProcess.has_subquestions,
        board_years: boardYears,
        tags,
        status: questionToProcess.status === '' || questionToProcess.status == null ? null : questionToProcess.status,
        is_active: questionToProcess.is_active !== undefined ? questionToProcess.is_active : true,
        exam_mode: questionToProcess.exam_mode ?? null,
      };
      if (!payload.has_subquestions) {
        payload.options = prepareOptionsForPayload(questionToProcess);
        payload.correct_answer = questionToProcess.correct_answer || '';
        payload.answer_evaluation = questionToProcess.answer_evaluation || {};
      } else {
        payload.options = [];
        payload.correct_answer = '';
        payload.answer_evaluation = {};
        payload.sub_questions_data = subQuestionsData;
      }
      // Only send image_url when it's a stored URL (http/https); omit proxy and blob URLs to avoid backend "invalid URL" validation
      if (questionToProcess.image_url === null) {
        payload.image_url = null;
      } else {
        const mainImageUrl = questionToProcess.image_url && typeof questionToProcess.image_url === 'string' ? questionToProcess.image_url.trim() : '';
        if (mainImageUrl && !mainImageUrl.startsWith('blob:') &&
            (mainImageUrl.startsWith('http://') || mainImageUrl.startsWith('https://')) &&
            !mainImageUrl.includes('/image-proxy/') && !mainImageUrl.includes('/image-proxy?')) {
          payload.image_url = mainImageUrl;
        }
      }
      console.log('QuestionBank - Final PATCH payload sub_questions_data:', JSON.stringify(payload.sub_questions_data, null, 2));
      const res = await api.patch(`${QUESTIONS}${questionToProcess.id}/`, payload);
      
      // If a new image was attached/pasted in the dialog, upload it
      if (imageFile) {
        const fd = new FormData();
        fd.append('image', imageFile);
        try {
          await api.post(`${QUESTIONS}${questionToProcess.id}/upload_image/`, fd);
        } catch (imgErr) {
          console.error('Image upload failed', imgErr);
          setSnackMessage(errorMessages.imageUploadFailed);
          setSnackOpen(true);
        }
      }
      let subQuestions = res?.data?.sub_questions || [];
      if (subQuestions.length === 0 && (Object.keys(subQuestionImageFiles || {}).length > 0 || Object.keys(subQuestionDetailImageFiles || {}).length > 0)) {
        try {
          const refetch = await api.get(`${QUESTIONS}${questionToProcess.id}/`);
          subQuestions = refetch?.data?.sub_questions || [];
        } catch (e) {
          console.warn('Could not refetch question for sub-question uploads', e);
        }
      }
      // Match files by index and by sub_question order (backend may return sub_questions in different order)
      const subQuestionsSorted = [...(subQuestions || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
      for (let i = 0; i < subQuestionsSorted.length; i++) {
        const sq = subQuestionsSorted[i];
        const file = subQuestionImageFiles?.[sq.order - 1] ?? subQuestionImageFiles?.[sq.order] ?? subQuestionImageFiles?.[i] ?? subQuestionImageFiles?.[String(i)];
        if (file && sq?.id) {
          const fd = new FormData();
          fd.append('image', file);
          const uploadUrl = `${QUESTIONS}${questionToProcess.id}/sub_questions/${sq.id}/upload_image/`;
          try {
            await api.post(uploadUrl, fd);
          } catch (imgErr) {
            console.error('Sub-question image upload failed', imgErr);
            setSnackMessage('Question updated; sub-question image upload failed.');
            setSnackOpen(true);
          }
        }
        const detailFile = subQuestionDetailImageFiles?.[sq.order - 1] ?? subQuestionDetailImageFiles?.[sq.order] ?? subQuestionDetailImageFiles?.[i] ?? subQuestionDetailImageFiles?.[String(i)];
        if (detailFile && sq?.id) {
          const fd = new FormData();
          fd.append('image', detailFile);
          const uploadUrl = `${QUESTIONS}${questionToProcess.id}/sub_questions/${sq.id}/upload_detail_answer_image/`;
          try {
            await api.post(uploadUrl, fd);
          } catch (imgErr) {
            console.error('Sub-question detail answer image upload failed', imgErr);
            setSnackMessage('Question updated; sub-question detail answer image upload failed.');
            setSnackOpen(true);
          }
        }
      }
      
      // Optimize: Update only the specific question in the questions array instead of fetching all
      const updatedQuestion = res.data;
      setQuestions(prevQuestions => 
        prevQuestions.map(q => 
          q.id === editQuestion.id 
            ? {
                ...q,
                ...updatedQuestion,
                board_years: (updatedQuestion.academic_years || []).map(ay => ({ 
                  board: ay.board_id, 
                  academic_year: ay.id 
                })),
                tags: (updatedQuestion.tags_detail || []).map(tag => tag.id)
              }
            : q
        )
      );
      
      // Show success message
      setSnackMessage('Question updated successfully!');
      setSnackOpen(true);
      
      // Close dialog and reset image state
      setOpenEdit(false);
      setNewImage(null);
      
      // Refresh from server to reflect latest ordering and computed fields
      await fetchQuestions(paginationModel.page + 1);
    } catch (err) {
      let errorMessage = 'Failed to update question.';
      if (err.response && err.response.data) {
        const flattened = flattenApiError(err.response.data);
        if (flattened) errorMessage = flattened;
      }
      setSnackMessage(errorMessage);
      setSnackOpen(true);
      console.error('Update question failed', err);
    }
  };

  const handleAddQuestion = async (processedQuestion = null, imageFile = null, subQuestionImageFiles = {}, subQuestionDetailImageFiles = {}) => {
    // Use processed question from QuestionDialog if provided, otherwise use state
    // imageFile: main-question image from dialog; subQuestionImageFiles: { index: File } for sub-question images
    const questionToProcess = processedQuestion || editQuestion;
    
    // Apply comprehensive validation before creating
    const validation = validateQuestion(questionToProcess);
    if (!validation.isValid) {
      const msg = validation.errors?.length ? validation.errors.join('. ') : 'Please fix the validation errors above.';
      setSnackMessage(msg);
      setSnackOpen(true);
      return;
    }
    
    // Prevent multiple submissions
    if (addingQuestion) {
      return;
    }
    
    setAddingQuestion(true);
    
    try {
      const payload = { ...questionToProcess };
      delete payload.academic_year;
      payload.board_years = (payload.board_years || []).map(p=>({ board: parseInt(p.board,10), academic_year: parseInt(p.academic_year,10), difficulty: parseInt(p.difficulty,10) }));
      if (!payload.has_subquestions) {
        payload.options = prepareOptionsForPayload(payload);
        payload.answer_evaluation = payload.answer_evaluation || {};
      } else {
        payload.options = [];
        payload.correct_answer = '';
        payload.answer_evaluation = {};
      }
      
      // Ensure tags are always an array of numbers (safety check)
      if (payload.tags && Array.isArray(payload.tags)) {
        payload.tags = payload.tags
          .map(tag => {
            if (typeof tag === 'number') return tag;
            if (typeof tag === 'string' && /^\d+$/.test(tag.trim())) return Number(tag.trim());
            if (tag && typeof tag === 'object' && tag.id && typeof tag.id === 'number') return tag.id;
            return null;
          })
          .filter(id => id !== null && typeof id === 'number' && id > 0);
      } else {
        payload.tags = [];
      }
      
      console.log('QuestionBank - Final payload tags:', payload.tags, 'all numbers?', payload.tags.every(id => typeof id === 'number'));
      console.log('QuestionBank - Payload has_subquestions:', payload.has_subquestions);
      console.log('QuestionBank - Payload sub_questions_data:', payload.sub_questions_data);
      console.log('QuestionBank - Full payload:', JSON.stringify(payload, null, 2));
      
      const res = await api.post(QUESTIONS, payload);
      console.log('QuestionBank - API response:', res.data);
      const newId = res?.data?.id;
      if (newId && imageFile) {
        const fd = new FormData();
        fd.append('image', imageFile);
        try {
          await api.post(`${QUESTIONS}${newId}/upload_image/`, fd);
        } catch (imgErr) {
          console.error('Image upload failed', imgErr);
          setSnackMessage(errorMessages.imageUploadFailed);
          setSnackOpen(true);
        }
      }
      let subQuestions = res?.data?.sub_questions || [];
      if (subQuestions.length === 0 && newId && (Object.keys(subQuestionImageFiles || {}).length > 0 || Object.keys(subQuestionDetailImageFiles || {}).length > 0)) {
        try {
          const refetch = await api.get(`${QUESTIONS}${newId}/`);
          subQuestions = refetch?.data?.sub_questions || [];
        } catch (e) {
          console.warn('Could not refetch question for sub-question uploads', e);
        }
      }
      // Match files by index and by sub_question order (backend may return sub_questions in different order)
      const subQuestionsSorted = [...(subQuestions || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
      for (let i = 0; i < subQuestionsSorted.length; i++) {
        const sq = subQuestionsSorted[i];
        const file = subQuestionImageFiles?.[sq.order - 1] ?? subQuestionImageFiles?.[sq.order] ?? subQuestionImageFiles?.[i] ?? subQuestionImageFiles?.[String(i)];
        if (file && sq?.id) {
          const fd = new FormData();
          fd.append('image', file);
          const uploadUrl = `${QUESTIONS}${newId}/sub_questions/${sq.id}/upload_image/`;
          try {
            await api.post(uploadUrl, fd);
          } catch (imgErr) {
            console.error('Sub-question image upload failed', imgErr);
            setSnackMessage('Question saved; sub-question image upload failed.');
            setSnackOpen(true);
          }
        }
        const detailFile = subQuestionDetailImageFiles?.[sq.order - 1] ?? subQuestionDetailImageFiles?.[sq.order] ?? subQuestionDetailImageFiles?.[i] ?? subQuestionDetailImageFiles?.[String(i)];
        if (detailFile && sq?.id) {
          const fd = new FormData();
          fd.append('image', detailFile);
          const uploadUrl = `${QUESTIONS}${newId}/sub_questions/${sq.id}/upload_detail_answer_image/`;
          try {
            await api.post(uploadUrl, fd);
          } catch (imgErr) {
            console.error('Sub-question detail answer image upload failed', imgErr);
            setSnackMessage('Question saved; sub-question detail answer image upload failed.');
            setSnackOpen(true);
          }
        }
      }
      // If there's temporary detail answer data, save it now
      if (tempDetailAnswerData && newId) {
        try {
          const detailAnswerPayload = {
            question: newId,
            answer: tempDetailAnswerData.answer || ''
          };
          
          const detailAnswerRes = await detailAnswerService.create(detailAnswerPayload);
          
          // If there's an image in the temp data, upload it
          if (tempDetailAnswerData.answerImage && detailAnswerRes.id) {
            try {
              const uploadResult = await detailAnswerService.uploadImage(detailAnswerRes.id, tempDetailAnswerData.answerImage);
              // Update the detail answer with the image URL
              await detailAnswerService.update(detailAnswerRes.id, {
                answer_image: uploadResult.url
              });
            } catch (imgErr) {
              console.error('Detail answer image upload failed', imgErr);
            }
          }
        } catch (detailErr) {
          console.error('Failed to save detail answer', detailErr);
        }
      }
      
      // Show success message
      setSnackMessage('Question added successfully!');
      setSnackOpen(true);
      
      // Close dialog and refresh
      handleCloseAddDialog();
      setAddingQuestion(false);
      
      // Refresh full list to include server timestamps/order and derived fields
      await fetchQuestions(1);
      fetchTotalInDb();
    } catch (err) {
      console.error('=== ERROR DETAILS START ===');
      console.error('Create question failed', err);
      console.error('Error type:', typeof err);
      console.error('Error constructor:', err.constructor.name);
      console.error('Error message:', err.message);
      console.error('Error stack:', err.stack);
      
      if (err.response) {
        console.error('Response status:', err.response.status);
        console.error('Response statusText:', err.response.statusText);
        console.error('Response headers:', err.response.headers);
        console.error('Response data:', err.response.data);
        console.error('Response data type:', typeof err.response.data);
        console.error('Response data keys:', Object.keys(err.response.data || {}));
      } else if (err.request) {
        console.error('Request was made but no response received:', err.request);
      } else {
        console.error('Error setting up request:', err.message);
      }
      
      console.error('Full error object:', JSON.stringify(err, null, 2));
      console.error('=== ERROR DETAILS END ===');
      
      // Show error message to user
      const errorMsg = err.response?.data?.detail || 
                      err.response?.data?.error?.[0] || 
                      err.response?.data?.message ||
                      err.message ||
                      'Failed to add question. Please check the console for details.';
      setSnackMessage(errorMsg);
      setSnackOpen(true);
      
      // Don't close dialog on error so user can fix issues
      setAddingQuestion(false);
    }
  };

  const saveDetailAnswerForQuestion = async (questionId, detailData) => {
    if (!questionId || !detailData) return;
    const payload = {
      question: questionId,
      answer: detailData.answer || '',
    };
    let savedDetailAnswer = null;
    const existingAnswers = await detailAnswerService.getByQuestionId(questionId);
    if (Array.isArray(existingAnswers) && existingAnswers.length > 0) {
      savedDetailAnswer = await detailAnswerService.update(existingAnswers[0].id, payload);
    } else {
      savedDetailAnswer = await detailAnswerService.create(payload);
    }
    if (detailData.answerImage && savedDetailAnswer?.id) {
      const uploadResult = await detailAnswerService.uploadImage(savedDetailAnswer.id, detailData.answerImage);
      await detailAnswerService.update(savedDetailAnswer.id, { answer_image: uploadResult.url });
    }
  };

  const handleUpdateMultipleQuestions = async (rowsToUpdate = [], imageFilesByRowId = {}, subQuestionImageFilesByRowId = {}, subQuestionDetailImageFilesByRowId = {}) => {
    if (!Array.isArray(rowsToUpdate) || rowsToUpdate.length === 0) {
      return { updatedRowIds: [], failedRowIds: [], failedRowErrors: {} };
    }
    setAddingQuestion(true);
    const updatedRowIds = [];
    const failedRowIds = [];
    const failedRowErrors = {};
    let success = 0;
    let failed = 0;
    const usedDetailKeys = [];
    try {
      for (const rowData of rowsToUpdate) {
        const { rowId, questionId, payload } = rowData || {};
        if (!rowId || !questionId || !payload) continue;
        try {
          let res;
          try {
            res = await api.patch(`${QUESTIONS}${questionId}/`, payload);
          } catch (err) {
            const examModeErrors = err?.response?.data?.exam_mode;
            const shouldRetryWithoutExamMode =
              payload.exam_mode &&
              Array.isArray(examModeErrors) &&
              examModeErrors.some((message) => typeof message === 'string' && /invalid pk/i.test(message));
            if (!shouldRetryWithoutExamMode) {
              throw err;
            }
            const { exam_mode: _examMode, ...payloadWithoutExamMode } = payload;
            console.warn('Retrying multi-question update without exam_mode after backend rejected the selected mode', {
              rowId,
              questionId,
              exam_mode: payload.exam_mode,
              examModeErrors,
            });
            res = await api.patch(`${QUESTIONS}${questionId}/`, payloadWithoutExamMode);
          }
          if (imageFilesByRowId[rowId]) {
            const fd = new FormData();
            fd.append('image', imageFilesByRowId[rowId]);
            try {
              await api.post(`${QUESTIONS}${questionId}/upload_image/`, fd);
            } catch (imgErr) {
              console.error('Multi update: main image upload failed', imgErr);
            }
          }
          let subQuestions = res?.data?.sub_questions || [];
          if (subQuestions.length === 0 && (Object.keys(subQuestionImageFilesByRowId[rowId] || {}).length > 0 || Object.keys(subQuestionDetailImageFilesByRowId[rowId] || {}).length > 0)) {
            try {
              const refetch = await api.get(`${QUESTIONS}${questionId}/`);
              subQuestions = refetch?.data?.sub_questions || [];
            } catch (e) {
              console.warn('Could not refetch updated question for sub-question uploads', e);
            }
          }
          const subFiles = subQuestionImageFilesByRowId[rowId] || {};
          const subDetailFiles = subQuestionDetailImageFilesByRowId[rowId] || {};
          const subQuestionsSorted = [...(subQuestions || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
          for (let j = 0; j < subQuestionsSorted.length; j++) {
            const sq = subQuestionsSorted[j];
            const file = subFiles[sq.order - 1] ?? subFiles[sq.order] ?? subFiles[j] ?? subFiles[String(j)];
            if (file && sq?.id) {
              const fd = new FormData();
              fd.append('image', file);
              try {
                await api.post(`${QUESTIONS}${questionId}/sub_questions/${sq.id}/upload_image/`, fd);
              } catch (imgErr) {
                console.error('Multi update: sub-question image upload failed', imgErr);
              }
            }
            const detailFile = subDetailFiles[sq.order - 1] ?? subDetailFiles[sq.order] ?? subDetailFiles[j] ?? subDetailFiles[String(j)];
            if (detailFile && sq?.id) {
              const fd = new FormData();
              fd.append('image', detailFile);
              try {
                await api.post(`${QUESTIONS}${questionId}/sub_questions/${sq.id}/upload_detail_answer_image/`, fd);
              } catch (imgErr) {
                console.error('Multi update: sub-question detail answer image upload failed', imgErr);
              }
            }
          }
          const detailKey = `multi-row-${rowId}`;
          const tempDetail = tempDetailByRowKey[detailKey];
          if (tempDetail) {
            try {
              await saveDetailAnswerForQuestion(questionId, tempDetail);
              usedDetailKeys.push(detailKey);
            } catch (detailErr) {
              console.error('Multi update: detail answer save failed', detailErr);
            }
          }
          updatedRowIds.push(rowId);
          success += 1;
        } catch (err) {
          failed += 1;
          failedRowIds.push(rowId);
          failedRowErrors[rowId] = flattenApiError(err?.response?.data) || err?.message || 'Update failed.';
          console.error('Multi update question failed', { rowId, questionId, payload }, err);
        }
      }

      if (usedDetailKeys.length > 0) {
        setTempDetailByRowKey((prev) => {
          const next = { ...prev };
          usedDetailKeys.forEach((k) => delete next[k]);
          return next;
        });
      }

      const msg = failed === 0
        ? `${success} question${success !== 1 ? 's' : ''} updated successfully.`
        : success > 0
          ? `${success} updated, ${failed} failed. Check console for details.`
          : 'Questions could not be updated. Check console for details.';
      setSnackMessage(msg);
      setSnackOpen(true);
      await fetchQuestions(1);
      fetchTotalInDb();
      return { updatedRowIds, failedRowIds, failedRowErrors };
    } finally {
      setAddingQuestion(false);
    }
  };

  const handleAddMultipleQuestions = async (payloads, rowIdsInOrder = [], imageFilesByRowId = {}, subQuestionImageFilesByRowId = {}, subQuestionDetailImageFilesByRowId = {}) => {
    if (!Array.isArray(payloads) || payloads.length === 0) return;
    setAddingQuestion(true);
    const rowIds = rowIdsInOrder.length === payloads.length ? rowIdsInOrder : payloads.map((_, i) => `row-${i}`);
    const createdRowIds = {};
    const failedRowIds = [];
    const failedRowErrors = {};
    let success = 0;
    let failed = 0;
    let duplicateCount = 0;
    const usedDetailKeys = [];
    try {
      for (let i = 0; i < payloads.length; i++) {
        const p = payloads[i];
        const rowId = rowIds[i];
        const payload = {
          ...p,
          board_years: (p.board_years || []).map(pair => ({
            board: parseInt(pair.board, 10),
            academic_year: parseInt(pair.academic_year, 10),
            difficulty: parseInt(pair.difficulty, 10),
          })),
          tags: Array.isArray(p.tags)
            ? [...new Set(p.tags
                .map(tag => {
                  if (typeof tag === 'number') return tag;
                  if (typeof tag === 'string' && /^\d+$/.test(tag.trim())) return Number(tag.trim());
                  if (tag && typeof tag === 'object' && typeof tag.id === 'number') return tag.id;
                  return null;
                })
                .filter(id => typeof id === 'number' && id > 0))]
            : [],
        };
        try {
          let res;
          try {
            res = await api.post(QUESTIONS, payload);
          } catch (err) {
            const examModeErrors = err?.response?.data?.exam_mode;
            const shouldRetryWithoutExamMode =
              payload.exam_mode &&
              Array.isArray(examModeErrors) &&
              examModeErrors.some((message) => typeof message === 'string' && /invalid pk/i.test(message));
            if (!shouldRetryWithoutExamMode) {
              throw err;
            }
            const { exam_mode: _examMode, ...payloadWithoutExamMode } = payload;
            console.warn('Retrying multi-question create without exam_mode after backend rejected the selected mode', {
              rowId,
              exam_mode: payload.exam_mode,
              examModeErrors,
            });
            res = await api.post(QUESTIONS, payloadWithoutExamMode);
          }
          const newId = res?.data?.id;
          if (newId) {
            createdRowIds[rowId] = newId;
            if (imageFilesByRowId[rowId]) {
              const fd = new FormData();
              fd.append('image', imageFilesByRowId[rowId]);
              try {
                await api.post(`${QUESTIONS}${newId}/upload_image/`, fd);
              } catch (imgErr) {
                console.error('Multi: main image upload failed', imgErr);
              }
            }
            let subQuestions = res?.data?.sub_questions || [];
            if (subQuestions.length === 0 && (Object.keys(subQuestionImageFilesByRowId[rowId] || {}).length > 0 || Object.keys(subQuestionDetailImageFilesByRowId[rowId] || {}).length > 0)) {
              try {
                const refetch = await api.get(`${QUESTIONS}${newId}/`);
                subQuestions = refetch?.data?.sub_questions || [];
              } catch (e) {
                console.warn('Could not refetch question for sub-question uploads', e);
              }
            }
            const subFiles = subQuestionImageFilesByRowId[rowId] || {};
            const subDetailFiles = subQuestionDetailImageFilesByRowId[rowId] || {};
            const subQuestionsSorted = [...(subQuestions || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
            for (let j = 0; j < subQuestionsSorted.length; j++) {
              const sq = subQuestionsSorted[j];
              const file = subFiles[sq.order - 1] ?? subFiles[sq.order] ?? subFiles[j] ?? subFiles[String(j)];
              if (file && sq?.id) {
                const fd = new FormData();
                fd.append('image', file);
                try {
                  await api.post(`${QUESTIONS}${newId}/sub_questions/${sq.id}/upload_image/`, fd);
                } catch (imgErr) {
                  console.error('Multi: sub-question image upload failed', imgErr);
                }
              }
              const detailFile = subDetailFiles[sq.order - 1] ?? subDetailFiles[sq.order] ?? subDetailFiles[j] ?? subDetailFiles[String(j)];
              if (detailFile && sq?.id) {
                const fd = new FormData();
                fd.append('image', detailFile);
                try {
                  await api.post(`${QUESTIONS}${newId}/sub_questions/${sq.id}/upload_detail_answer_image/`, fd);
                } catch (imgErr) {
                  console.error('Multi: sub-question detail answer image upload failed', imgErr);
                }
              }
            }
            const detailKey = `multi-row-${rowId}`;
            const tempDetail = tempDetailByRowKey[detailKey];
            if (tempDetail && newId) {
              try {
                const detailAnswerPayload = { question: newId, answer: tempDetail.answer || '' };
                const detailAnswerRes = await detailAnswerService.create(detailAnswerPayload);
                if (tempDetail.answerImage && detailAnswerRes.id) {
                  try {
                    const uploadResult = await detailAnswerService.uploadImage(detailAnswerRes.id, tempDetail.answerImage);
                    await detailAnswerService.update(detailAnswerRes.id, { answer_image: uploadResult.url });
                  } catch (imgErr) {
                    console.error('Multi: detail answer image upload failed', imgErr);
                  }
                }
              } catch (detailErr) {
                console.error('Multi: detail answer create failed', detailErr);
              }
              usedDetailKeys.push(detailKey);
            }
          }
          success += 1;
        } catch (err) {
          failed += 1;
          failedRowIds.push(rowId);
          failedRowErrors[rowId] = flattenApiError(err?.response?.data) || err?.message || 'Create failed.';
          const data = err?.response?.data;
          const detail = data?.detail;
          const detailStr = typeof detail === 'string' ? detail : (detail && JSON.stringify(detail));
          const fullStr = detailStr || (data && JSON.stringify(data));
          if (fullStr && /duplicate/i.test(fullStr)) duplicateCount += 1;
          console.error('Multi add question failed for payload', payload, err);
        }
      }
      if (usedDetailKeys.length > 0) {
        setTempDetailByRowKey((prev) => {
          const next = { ...prev };
          usedDetailKeys.forEach((k) => delete next[k]);
          return next;
        });
      }
      let msg;
      if (failed === 0) {
        msg = `${success} question${success !== 1 ? 's' : ''} added successfully.`;
      } else if (duplicateCount === failed) {
        msg = 'Duplicate questions were not added.';
      } else if (success > 0) {
        msg = `${success} added, ${failed} failed. Check console for details.`;
      } else {
        msg = duplicateCount > 0 ? 'Duplicate questions were not added.' : 'Questions could not be added. Check console for details.';
      }
      setSnackMessage(msg);
      setSnackOpen(true);
      await fetchQuestions(1);
      fetchTotalInDb();
      return { createdRowIds, failedRowIds, failedRowErrors };
    } finally {
      setAddingQuestion(false);
    }
  };

  const handleOpenDetailAnswer = async (questionId) => {
    setCurrentQuestionId(questionId);
    setOpenDetailAnswer(true);
    if (questionId === 'new') {
      setCurrentDetailAnswer(tempDetailAnswerData);
      setDetailAnswerLoading(false);
      return;
    }
    if (typeof questionId === 'string' && questionId.startsWith('multi-row-')) {
      setCurrentDetailAnswer(tempDetailByRowKey[questionId] || null);
      setDetailAnswerLoading(false);
      return;
    }
    setDetailAnswerLoading(true);
    setCurrentDetailAnswer(null);
    detailAnswerService.getByQuestionId(questionId)
      .then((existingAnswers) => {
        if (existingAnswers && Array.isArray(existingAnswers) && existingAnswers.length > 0) {
          setCurrentDetailAnswer(existingAnswers[0]);
        } else {
          setCurrentDetailAnswer(null);
        }
      })
      .catch((err) => {
        console.error('Failed to fetch existing detail answer', err);
        setCurrentDetailAnswer(null);
      })
      .finally(() => setDetailAnswerLoading(false));
  };

  const handleDetailAnswerSave = async (detailAnswerData) => {
    if (currentQuestionId === 'new') {
      setTempDetailAnswerData(detailAnswerData);
      setOpenDetailAnswer(false);
      return;
    }
    if (currentQuestionId && typeof currentQuestionId === 'string' && currentQuestionId.startsWith('multi-row-')) {
      setTempDetailByRowKey((prev) => ({ ...prev, [currentQuestionId]: detailAnswerData }));
      setOpenDetailAnswer(false);
      return;
    }

    // For existing questions, refresh the data
    try {
      // Fetch the updated detail answer to ensure we have the latest data
      const updatedAnswers = await detailAnswerService.getByQuestionId(currentQuestionId);
      if (updatedAnswers.length > 0) {
        setCurrentDetailAnswer(updatedAnswers[0]);
      }
      // Trigger refresh of detail answer check in QuestionDialog
      setDetailAnswerRefreshTrigger(prev => prev + 1);
    } catch (err) {
      console.error('Failed to refresh detail answer data', err);
    }
  };

  const handleCloseAddDialog = () => {
    resetEditQuestion();
    setTempDetailAnswerData(null);
  };

  const handleDownloadTemplate = useCallback(async () => {
    try {
      const response = await api.get('/exams/questions/template/', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'questions_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download template:', error);
    }
  }, []);

  // Simple inline handlers for menu - no memoization to avoid delays

  const FilterPanel = ({ variant = 'side' }) => {
    const textFieldStyles = teacherFilterFieldSx;
    const compactFieldStyles = teacherFilterCompactFieldSx;
    const sectionStyles = {
      minWidth: 260,
      flex: '1 1 300px',
      pr: { lg: 1.5, xl: 2 },
      borderRight: '1px solid',
      borderColor: 'divider',
    };
    const sectionGridStyles = {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
      gap: 1,
      alignItems: 'start',
      '& .MuiTextField-root, & .MuiAutocomplete-root': {
        width: '100%',
        minWidth: 0,
      },
    };
    const actionSectionStyles = {
      minWidth: 220,
      flex: '0 1 240px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
      justifyContent: 'flex-start',
      gap: 1,
      pt: 2.75,
    };

    if (variant === 'top') {
      return (
        <Paper
          variant="outlined"
          sx={{
            ...teacherFilterPanelSx(true),
            mb: { lg: 2, xl: 2.5 },
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { lg: 'repeat(auto-fit, minmax(260px, 1fr))' },
              gap: { lg: 1.5, xl: 2 },
              alignItems: 'start',
            }}
          >
            <Box sx={sectionStyles}>
              <Typography variant="caption" sx={teacherFilterSectionLabelSx}>
                Academic Context
              </Typography>
              <Box sx={sectionGridStyles}>
                <TextField select label="Board" size="small" value={filters.board} onChange={handleFilterChange('board')} sx={compactFieldStyles}>
                  <MenuItem value="">All Boards</MenuItem>
                  {boards.map((b) => (
                    <MenuItem key={b.id} value={b.id}>{b.name || b.board}</MenuItem>
                  ))}
                </TextField>
                <TextField select label="Year" size="small" value={filters.year} onChange={handleFilterChange('year')} sx={compactFieldStyles}>
                  <MenuItem value="">All Years</MenuItem>
                  {academicYears.map((y) => (
                    <MenuItem key={y.id} value={y.id}>{y.year}</MenuItem>
                  ))}
                </TextField>
                <TextField select label="Subject" size="small" value={filters.subject} onChange={handleFilterChange('subject')} sx={compactFieldStyles}>
                  <MenuItem value="">All Subjects</MenuItem>
                  {subjects.map((s) => (
                    <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                  ))}
                </TextField>
                <Autocomplete
                  size="small"
                  options={topics}
                  getOptionLabel={(option) => {
                    if (!option) return '';
                    const name = option.topic || option.name || '';
                    const count = filters.subject
                      ? (topicCountsById[String(option.id)] ?? 0)
                      : (topicCountsById[String(option.id)] ?? option.question_count);
                    return typeof count === 'number' ? `${name} (${count})` : name;
                  }}
                  value={topics.find((t) => String(t.id) === String(filters.topic)) || null}
                  onChange={(e, newValue) =>
                    setFilters((prev) => ({ ...prev, topic: newValue ? newValue.id : '' }))
                  }
                  isOptionEqualToValue={(option, value) => option && value && option.id === value.id}
                  disabled={!filters.board || !filters.year || !filters.subject}
                  renderInput={(params) => (
                    <TextField {...params} label="Topic" placeholder="Search topic" sx={{ ...compactFieldStyles, minWidth: { lg: 180, xl: 220 } }} />
                  )}
                  noOptionsText="No topics mapped to this curriculum"
                  sx={{ minWidth: 0, gridColumn: { lg: 'auto', xl: 'auto' } }}
                />
              </Box>
            </Box>

            <Box sx={sectionStyles}>
              <Typography variant="caption" sx={teacherFilterSectionLabelSx}>
                Question Details
              </Typography>
              <Box sx={sectionGridStyles}>
                <TextField select label="Difficulty" size="small" value={filters.difficulty} onChange={handleFilterChange('difficulty')} sx={compactFieldStyles}>
                  <MenuItem value="">All Levels</MenuItem>
                  {difficulties.map((d) => (
                    <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                  ))}
                </TextField>
                <TextField select label="Type" size="small" value={filters.type} onChange={handleFilterChange('type')} sx={compactFieldStyles}>
                  <MenuItem value="">All Types</MenuItem>
                  {TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                  ))}
                </TextField>
                <TextField select label="Correct answer" size="small" value={filters.hasCorrectAnswer} onChange={handleFilterChange('hasCorrectAnswer')} sx={compactFieldStyles}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </TextField>
                <TextField select label="Detail answer" size="small" value={filters.hasDetailAnswer} onChange={handleFilterChange('hasDetailAnswer')} sx={compactFieldStyles}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </TextField>
                <TextField select label="Question image" size="small" value={filters.hasImage} onChange={handleFilterChange('hasImage')} sx={{ ...compactFieldStyles, minWidth: { lg: 160, xl: 190 } }}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Yes - main or subquestion</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </TextField>
              </Box>
            </Box>

            <Box sx={sectionStyles}>
              <Typography variant="caption" sx={teacherFilterSectionLabelSx}>
                Administration
              </Typography>
              <Box sx={sectionGridStyles}>
                <TextField select label="Published" size="small" value={filters.status} onChange={handleFilterChange('status')} sx={compactFieldStyles}>
                  <MenuItem value="">All Statuses</MenuItem>
                  {statuses.length > 0 ? (
                    statuses.map((status) => (
                      <MenuItem key={status.id} value={status.id}>
                        {status.value}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="" disabled>
                      Loading statuses...
                    </MenuItem>
                  )}
                </TextField>
                <TextField select label="Used In" size="small" value={filters.usedIn} onChange={handleFilterChange('usedIn')} sx={compactFieldStyles}>
                  <MenuItem value="">All modes</MenuItem>
                  {(examModes || []).map(m => (
                    <MenuItem key={m.id} value={m.id}>{m.mode}</MenuItem>
                  ))}
                </TextField>
                <Autocomplete
                  options={availableTags}
                  value={availableTags.find(tag => String(tag.id) === String(filters.tags)) || null}
                  onChange={(event, newValue) => {
                    setFilters(prev => ({ ...prev, tags: newValue ? newValue.id : '' }));
                  }}
                  getOptionLabel={(option) => option.name || ''}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField {...params} label="Tags" size="small" sx={compactFieldStyles} />
                  )}
                  noOptionsText="No tags found"
                  sx={{ minWidth: 0 }}
                />
                <TextField select label="Generated by AI" size="small" value={filters.aiGenerated} onChange={handleFilterChange('aiGenerated')} sx={{ ...compactFieldStyles, minWidth: { lg: 150, xl: 180 } }}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </TextField>
              </Box>
            </Box>

            <Box sx={actionSectionStyles}>
              {!hideSelfOnlyToggle && !showAllQuestions && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={selfOnly}
                      onChange={(e) => setSelfOnly(e.target.checked)}
                      color="primary"
                      size="small"
                    />
                  }
                  label={selfOnly ? "My Questions Only" : "All Questions"}
                  sx={teacherFilterToggleSx(true)}
                />
              )}
              <Button
                size="small"
                startIcon={<ClearIcon />}
                onClick={clearFilters}
                disabled={!Object.values(filters).some(v => v !== '')}
                sx={teacherFilterClearButtonSx}
              >
                Clear All
              </Button>
            </Box>
          </Box>
        </Paper>
      );
    }

    return (
      <Paper 
        elevation={2} 
        sx={teacherFilterPanelSx(false, 0)}
      >
        <Box sx={{ mb: 2 }}>
          <Box sx={teacherFilterHeaderSx(false)}>
            <Typography variant="h6" sx={teacherFilterTitleSx(false)}>
              Filters
            </Typography>
            <Button
              size="small"
              startIcon={<ClearIcon />}
              onClick={clearFilters}
              disabled={!Object.values(filters).some(v => v !== '')}
              sx={teacherFilterClearButtonSx}
            >
              Clear All
            </Button>
          </Box>
          {/* Toggle switch with dynamic label */}
          {!hideSelfOnlyToggle && !showAllQuestions && (
            <FormControlLabel
              control={
                <Switch
                  checked={selfOnly}
                  onChange={(e) => setSelfOnly(e.target.checked)}
                  color="primary"
                />
              }
              label={selfOnly ? "My Questions Only" : "All Questions"}
              sx={teacherFilterToggleSx(false)}
            />
          )}
        </Box>

        <Box
          sx={{
            overflowY: 'auto',
            maxHeight: { xs: 'calc(100vh - 220px)', lg: 'calc(100vh - 190px)', xl: 'calc(100vh - 220px)' },
            pr: 0.5
          }}
        >
          <Stack spacing={{ lg: 0.75, xl: 1 }}>
          {/* Curriculum Section */}
          <Accordion defaultExpanded sx={{ boxShadow: 'none', border: '1px solid', borderColor: 'divider', borderRadius: 2, '&:before': { display: 'none' } }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ px: 2, py: 1 }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Curriculum
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, px: 2, pb: 2 }}>
              <Stack spacing={2}>
                <TextField 
                  select 
                  label="Board" 
                  size="small" 
                  value={filters.board} 
                  onChange={handleFilterChange('board')}
                  sx={textFieldStyles}
                >
                  <MenuItem value="">All Boards</MenuItem>
                  {boards.map((b) => (
                    <MenuItem key={b.id} value={b.id}>{b.name || b.board}</MenuItem>
                  ))}
                </TextField>

                <TextField 
                  select 
                  label="Academic Year" 
                  size="small" 
                  value={filters.year} 
                  onChange={handleFilterChange('year')}
                  sx={textFieldStyles}
                >
                  <MenuItem value="">All Years</MenuItem>
                  {academicYears.map((y) => (
                    <MenuItem key={y.id} value={y.id}>{y.year}</MenuItem>
                  ))}
                </TextField>
                
                <TextField 
                  select 
                  label="Subject" 
                  size="small" 
                  value={filters.subject} 
                  onChange={handleFilterChange('subject')}
                  sx={textFieldStyles}
                >
                  <MenuItem value="">All Subjects</MenuItem>
                  {subjects.map((s) => (
                    <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                  ))}
                </TextField>
                
                <Autocomplete
                  size="small"
                  options={topics}
                  getOptionLabel={(option) => {
                    if (!option) return '';
                    const name = option.topic || option.name || '';
                    const count = filters.subject
                      ? (topicCountsById[String(option.id)] ?? 0)
                      : (topicCountsById[String(option.id)] ?? option.question_count);
                    return typeof count === 'number' ? `${name} (${count})` : name;
                  }}
                  value={topics.find((t) => String(t.id) === String(filters.topic)) || null}
                  onChange={(e, newValue) =>
                    setFilters((prev) => ({ ...prev, topic: newValue ? newValue.id : '' }))
                  }
                  isOptionEqualToValue={(option, value) => option && value && option.id === value.id}
                  disabled={!filters.board || !filters.year || !filters.subject}
                  renderInput={(params) => (
                    <TextField {...params} label="Topic" placeholder="Search or select topic" sx={textFieldStyles} />
                  )}
                  noOptionsText="No topics mapped to this curriculum"
                />
              </Stack>
            </AccordionDetails>
          </Accordion>

          {/* Classification Section */}
          <Accordion defaultExpanded sx={{ boxShadow: 'none', border: '1px solid', borderColor: 'divider', borderRadius: 2, '&:before': { display: 'none' } }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ px: 2, py: 1 }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Classification
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, px: 2, pb: 2 }}>
              <Stack spacing={2}>
                <TextField 
                  select 
                  label="Difficulty" 
                  size="small" 
                  value={filters.difficulty} 
                  onChange={handleFilterChange('difficulty')}
                  sx={textFieldStyles}
                >
                  <MenuItem value="">All Levels</MenuItem>
                  {difficulties.map((d) => (
                    <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                  ))}
                </TextField>
                
                <TextField 
                  select 
                  label="Type" 
                  size="small" 
                  value={filters.type} 
                  onChange={handleFilterChange('type')}
                  sx={textFieldStyles}
                >
                  <MenuItem value="">All Types</MenuItem>
                  {TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                  ))}
                </TextField>
              </Stack>
            </AccordionDetails>
          </Accordion>

          {/* Status & Usage Section */}
          <Accordion defaultExpanded sx={{ boxShadow: 'none', border: '1px solid', borderColor: 'divider', borderRadius: 2, '&:before': { display: 'none' } }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ px: 2, py: 1 }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Status & Usage
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, px: 2, pb: 2 }}>
              <Stack spacing={2}>
                <TextField 
                  select 
                  label="Published" 
                  size="small" 
                  value={filters.status} 
                  onChange={handleFilterChange('status')}
                  sx={textFieldStyles}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  {statuses.length > 0 ? (
                    statuses.map((status) => (
                      <MenuItem key={status.id} value={status.id}>
                        {status.value}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="" disabled>
                      Loading statuses...
                    </MenuItem>
                  )}
                </TextField>
                
                <TextField 
                  select 
                  label="Used In" 
                  size="small" 
                  value={filters.usedIn} 
                  onChange={handleFilterChange('usedIn')}
                  sx={textFieldStyles}
                >
                  <MenuItem value="">All modes</MenuItem>
                  {(examModes || []).map(m => (
                    <MenuItem key={m.id} value={m.id}>{m.mode}</MenuItem>
                  ))}
                </TextField>
              </Stack>
            </AccordionDetails>
          </Accordion>

          {/* Answers Section */}
          <Accordion defaultExpanded sx={{ boxShadow: 'none', border: '1px solid', borderColor: 'divider', borderRadius: 2, '&:before': { display: 'none' } }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ px: 2, py: 1 }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Answers
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, px: 2, pb: 2 }}>
              <Stack spacing={2}>
                <TextField
                  select
                  label="Correct answer present"
                  size="small"
                  value={filters.hasCorrectAnswer}
                  onChange={handleFilterChange('hasCorrectAnswer')}
                  sx={textFieldStyles}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </TextField>
                <TextField
                  select
                  label="Detail answer present?"
                  size="small"
                  value={filters.hasDetailAnswer}
                  onChange={handleFilterChange('hasDetailAnswer')}
                  sx={textFieldStyles}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </TextField>
                <TextField
                  select
                  label="Question image present?"
                  size="small"
                  value={filters.hasImage}
                  onChange={handleFilterChange('hasImage')}
                  sx={textFieldStyles}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Yes — main or subquestion</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </TextField>
              </Stack>
            </AccordionDetails>
          </Accordion>

          {/* Tags Section */}
          <Accordion defaultExpanded sx={{ boxShadow: 'none', border: '1px solid', borderColor: 'divider', borderRadius: 2, '&:before': { display: 'none' } }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ px: 2, py: 1 }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Tags
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, px: 2, pb: 2 }}>
              <Stack spacing={2}>
                <Autocomplete
                  options={availableTags}
                  value={availableTags.find(tag => String(tag.id) === String(filters.tags)) || null}
                  onChange={(event, newValue) => {
                    setFilters(prev => ({ ...prev, tags: newValue ? newValue.id : '' }));
                  }}
                  getOptionLabel={(option) => option.name || ''}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  filterOptions={(options, params) => {
                    const filtered = options.filter(option =>
                      option.name.toLowerCase().includes(params.inputValue.toLowerCase())
                    );
                    return filtered;
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Tag"
                      size="small"
                      sx={textFieldStyles}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            backgroundColor: option.color || '#1976d2',
                            borderRadius: 1,
                            border: '1px solid #ddd',
                            flexShrink: 0
                          }}
                          aria-hidden="true"
                        />
                        <Typography variant="body2">
                          {option.name}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  noOptionsText="No tags found"
                />
              </Stack>
            </AccordionDetails>
          </Accordion>

          {/* AI Generated Section */}
          <Accordion defaultExpanded sx={{ boxShadow: 'none', border: '1px solid', borderColor: 'divider', borderRadius: 2, '&:before': { display: 'none' } }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ px: 2, py: 1 }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                AI Generation
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, px: 2, pb: 2 }}>
              <Stack spacing={2}>
                <TextField 
                  select 
                  label="Generated by AI" 
                  size="small" 
                  value={filters.aiGenerated} 
                  onChange={handleFilterChange('aiGenerated')}
                  sx={textFieldStyles}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </TextField>
              </Stack>
            </AccordionDetails>
          </Accordion>
          </Stack>
        </Box>
      </Paper>
    );
  };

  const EmptyState = () => (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center',
      py: 8,
      textAlign: 'center'
    }}>
      <Box sx={{ 
        width: 80, 
        height: 80, 
        borderRadius: '50%', 
        backgroundColor: 'grey.100',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mb: 3
      }}>
        <QuestionMarkIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
      </Box>
      <Typography variant="h6" sx={{ mb: 1, color: 'text.secondary' }}>
        No questions found
      </Typography>
      <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
        Try adjusting your filters or add your first question.
      </Typography>
    </Box>
  );

  // File change handled in QuestionUploadDialog

  return (
    <>
      {/* Mobile Filter Drawer */}
      <Drawer
        anchor="left"
        open={mobileFilterOpen}
        onClose={() => setMobileFilterOpen(false)}
        sx={{ display: { lg: 'none' } }}
      >
        <Box sx={{ width: 280, p: 2 }}>
          <FilterPanel />
        </Box>
      </Drawer>

      <Box sx={{ p: { xs: 1.5, sm: 2, lg: 2, xl: 3 }, overflowX: 'hidden', minWidth: 0 }}>
            {/* Desktop Layout – align filter card to top of main content */}
            <Box sx={{ display: { xs: 'none', lg: 'block' }, minWidth: 0 }}>
              <Box sx={{ minWidth: 0 }}>
                <Paper
                  elevation={2}
                  sx={{
                    p: { lg: 2, xl: 3 },
                    ...commonStyles,
                    overflow: 'hidden',
                  }}
                >
{/* Header */}
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'flex-start', 
                    mb: { lg: 2, xl: 3 },
                    flexWrap: 'wrap',
                    gap: { lg: 0.75, xl: 1 }
                  }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mb: 1, fontSize: { lg: '1.35rem', xl: '1.5rem' } }}>
                        Question Bank
                      </Typography>
                      <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: { lg: '0.95rem', xl: '1rem' } }}>
                        Total Questions: {totalCount != null ? totalCount : '—'}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Tabs */}
                  <Box sx={{ mb: { lg: 2, xl: 3 } }}>
                    <Tabs 
                      value={activeTab} 
                      onChange={(e, newValue) => setActiveTab(newValue)}
                      sx={{
                        '& .MuiTab-root': {
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: { lg: '0.9rem', xl: '1rem' },
                          minHeight: { lg: 42, xl: 48 },
                        },
                        '& .Mui-selected': {
                          color: 'primary.main',
                        },
                      }}
                    >
                      <Tab label="Questions" />
                      <Tab label="Report" />
                    </Tabs>
                  </Box>

                  <FilterPanel variant="top" />

                  {/* Tab Content */}
                  {activeTab === 0 && (
                    <>
                      {/* Question Bank Content */}
                      {/* Action Buttons */}
                      <Box sx={{ mb: { lg: 2, xl: 3 } }}>
                        <Box sx={{ display: 'flex', gap: { lg: 0.75, xl: 1 }, justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap' }}>
                          <BulkActionsDropdown
                            onUploadInstructions={() => setOpenInstr(true)}
                            onDownloadTemplate={handleDownloadTemplate}
                            onUploadQuestions={() => setOpenUpload(true)}
                            buttonSx={bulkActionsButtonStyles}
                          />
                          <AddQuestionButtonAndDialog
                            initialQuestion={INITIAL_QUESTION}
                            onSave={handleAddQuestion}
                            onSaveMultiple={handleAddMultipleQuestions}
                            onUpdateRows={handleUpdateMultipleQuestions}
                            onDeleteRow={handleDelete}
                            onCloseCreateDialog={() => {
                              resetEditQuestion();
                              setTempDetailAnswerData(null);
                            }}
                            subjects={subjects}
                            allTopics={allTopics}
                            boards={boards}
                            academicYears={academicYears}
                            curriculumAssociations={curriculumAssociations}
                            difficulties={difficulties}
                            examModes={examModes}
                            onOpenDetailAnswer={handleOpenDetailAnswer}
                            statuses={dialogStatuses}
                            isChampions={role === 'champion'}
                            role={role}
                            detailAnswerRefreshTrigger={detailAnswerRefreshTrigger}
                            buttonSx={addQuestionButtonStyles}
                          />

                          {role !== 'champion' && (
                            <>
                              <AddWithAIButtonAndDialog
                                subjects={subjects}
                                allTopics={allTopics}
                                boards={boards}
                                academicYears={academicYears}
                                curriculumAssociations={curriculumAssociations}
                                difficulties={difficulties}
                                onSuccess={(count) => {
                                  setSnackMessage(count != null ? `${count} question${count !== 1 ? 's' : ''} added with AI.` : 'Questions added successfully.');
                                  setSnackOpen(true);
                                  fetchQuestions(1);
                                  fetchTotalInDb();
                                }}
                                onError={(msg) => {
                                  setSnackMessage(msg || 'Failed to add questions.');
                                  setSnackOpen(true);
                                }}
                                buttonSx={addQuestionButtonStyles}
                              />
                              <UpdateWithAIButtonAndDialog
                                subjects={subjects}
                                allTopics={allTopics}
                                boards={boards}
                                academicYears={academicYears}
                                difficulties={difficulties}
                                onSuccess={(count) => {
                                  const base = count && count > 0
                                    ? `${count} update${count !== 1 ? 's' : ''} applied with AI.`
                                    : 'No updates were applied.';
                                  setSnackMessage(base);
                                  setSnackOpen(true);
                                  fetchQuestions(1);
                                }}
                                onError={(msg) => {
                                  setSnackMessage(msg || 'Failed to update answers with AI.');
                                  setSnackOpen(true);
                                }}
                                buttonSx={addQuestionButtonStyles}
                              />
                            </>
                          )}
                        </Box>
                      </Box>

                      {/* DataGrid */}
                      {loading ? (
                        <Box sx={loadingBoxStyle}>
                          <CircularProgress />
                        </Box>
                      ) : filteredRows.length === 0 ? (
                        <EmptyState />
) : (
                      <Box sx={{ overflowX: 'auto', minWidth: 0, width: '100%' }}>
                        <DataGrid
                          rows={filteredRows}
                          columns={columns}
                          {...dataGridConfig}
                          sx={{ ...dataGridStyles, minWidth: { lg: 560, xl: 600 } }}
                          components={{
                            Toolbar: () => (
                              <Box sx={{ ...toolbarBoxStyle, flexWrap: 'wrap' }}>
                                <GridToolbarColumnsButton />
                                <GridToolbarDensitySelector />
                              </Box>
                            ),
                          }}
                        />
                      </Box>
                    )}
                  </>
                )}

                {activeTab === 1 && (
                  <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>}>
                    {analyticsLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress />
                      </Box>
                    ) : (
                      <QuestionAnalytics
                        questions={analyticsQuestions}
                        allTopics={allTopics}
                        curriculumAssociations={curriculumAssociations}
                        role={role}
                        selfOnly={selfOnly}
                        userId={user?.id}
                        filters={filters}
                      />
                    )}
                  </Suspense>
                )}


                </Paper>
              </Box>
            </Box>

            {/* Mobile Layout — summary card, tabs, horizontal actions, question cards */}
            <Box sx={{ display: { xs: 'block', lg: 'none' }, minWidth: 0 }}>
              <Paper 
                elevation={2} 
                sx={{ 
                  p: { xs: 2, sm: 3 }, 
                  ...commonStyles
                }}
              >
                <SummaryCard 
                  label="Total Questions" 
                  value={totalCount != null ? totalCount : '—'} 
                  icon={QuestionMarkIcon} 
                />

                <Box sx={{ mb: 2 }}>
                  <Tabs 
                    value={activeTab} 
                    onChange={(e, newValue) => setActiveTab(newValue)}
                    sx={{
                      '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '1rem', minHeight: 48 },
                      '& .Mui-selected': { color: 'primary.main' },
                    }}
                  >
                    <Tab label="Question Bank" />
                    <Tab label="Report" />
                  </Tabs>
                </Box>

                {activeTab === 0 && (
                  <>
                    {/* Horizontally scrollable action bar — visible scrollbar + right fade so users see more actions */}
                    <Box sx={{ position: 'relative', mb: 2 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 1,
                          overflowX: 'auto',
                          overflowY: 'hidden',
                          pb: 1,
                          flexShrink: 0,
                          WebkitOverflowScrolling: 'touch',
                          '&::-webkit-scrollbar': { height: 8 },
                          '&::-webkit-scrollbar-track': { bgcolor: 'action.hover', borderRadius: 1 },
                          '&::-webkit-scrollbar-thumb': { bgcolor: 'primary.main', borderRadius: 1 },
                          '&::-webkit-scrollbar-thumb:hover': { bgcolor: 'primary.dark' },
                        }}
                      >
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<FilterListIcon />}
                        onClick={() => setMobileFilterOpen(true)}
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, flexShrink: 0 }}
                      >
                        Filters
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<WarningAmberIcon />}
                        onClick={() => setOpenInstr(true)}
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, flexShrink: 0 }}
                      >
                        Instructions
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownloadTemplate}
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, flexShrink: 0 }}
                      >
                        Template
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<UploadFileIcon />}
                        onClick={() => setOpenUpload(true)}
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, flexShrink: 0 }}
                      >
                        Upload
                      </Button>
                      <AddQuestionButtonAndDialog
                        initialQuestion={INITIAL_QUESTION}
                        onSave={handleAddQuestion}
                        onSaveMultiple={handleAddMultipleQuestions}
                        onUpdateRows={handleUpdateMultipleQuestions}
                        onDeleteRow={handleDelete}
                        onCloseCreateDialog={() => {
                          resetEditQuestion();
                          setTempDetailAnswerData(null);
                        }}
                        subjects={subjects}
                        allTopics={allTopics}
                        boards={boards}
                        academicYears={academicYears}
                        curriculumAssociations={curriculumAssociations}
                        difficulties={difficulties}
                        examModes={examModes}
                        onOpenDetailAnswer={handleOpenDetailAnswer}
                        statuses={dialogStatuses}
                        isChampions={role === 'champion'}
                        role={role}
                        detailAnswerRefreshTrigger={detailAnswerRefreshTrigger}
                        buttonSx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, flexShrink: 0 }}
                        size="small"
                        buttonText="Add"
                      />
                      {role !== 'champion' && (
                        <>
                          <AddWithAIButtonAndDialog
                            subjects={subjects}
                            allTopics={allTopics}
                            boards={boards}
                            academicYears={academicYears}
                            curriculumAssociations={curriculumAssociations}
                            difficulties={difficulties}
                            onSuccess={(count) => {
                              setSnackMessage(count != null ? `${count} question${count !== 1 ? 's' : ''} added with AI.` : 'Questions added successfully.');
                              setSnackOpen(true);
                              fetchQuestions(1);
                              fetchTotalInDb();
                            }}
                            onError={(msg) => {
                              setSnackMessage(msg || 'Failed to add questions.');
                              setSnackOpen(true);
                            }}
                            buttonSx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, flexShrink: 0 }}
                            size="small"
                          />
                          <UpdateWithAIButtonAndDialog
                            subjects={subjects}
                            allTopics={allTopics}
                            boards={boards}
                            academicYears={academicYears}
                            difficulties={difficulties}
                            onSuccess={(count) => {
                              const base = count && count > 0
                                ? `${count} update${count !== 1 ? 's' : ''} applied with AI.`
                                : 'No updates were applied.';
                              setSnackMessage(base);
                              setSnackOpen(true);
                              fetchQuestions(1);
                            }}
                            onError={(msg) => {
                              setSnackMessage(msg || 'Failed to update answers with AI.');
                              setSnackOpen(true);
                            }}
                            buttonSx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, flexShrink: 0 }}
                            size="small"
                          />
                        </>
                      )}
                      </Box>
                      {/* Right-edge fade to hint more buttons (scroll to see Template, Upload, Add) */}
                      <Box
                        sx={{
                          position: 'absolute',
                          right: 0,
                          top: 0,
                          bottom: 8,
                          width: 28,
                          pointerEvents: 'none',
                          background: (t) => `linear-gradient(to right, transparent 0%, ${t.palette.background.paper} 100%)`,
                        }}
                      />
                    </Box>

                    {loading ? (
                      <Box sx={loadingBoxStyle}>
                        <CircularProgress />
                      </Box>
                    ) : filteredRows.length === 0 ? (
                      <EmptyState />
                    ) : (
                      <Stack spacing={2}>
                        {filteredRows.map((row) => {
                          const canEdit = role === 'admin'
                            || (getCreatorId(row) === String(user?.id) && row.used_in_tests === 0);
                          const canDelete = row.used_in_tests === 0 && getCreatorId(row) === String(user?.id);
                          const difficultyLabel = (row.academic_years && row.academic_years[0]?.difficulty) || '';
                          const topicLabel = row.topic_name || '—';
                          return (
                            <Paper
                              key={row.id}
                              variant="outlined"
                              sx={{ p: 2, borderRadius: 2 }}
                            >
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                <Chip label={`ID: #Q-${row.id}`} size="small" color="primary" sx={{ fontWeight: 600 }} />
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setActionMenuAnchor(e.currentTarget);
                                    setSelectedRow(row);
                                  }}
                                  aria-label="More actions"
                                >
                                  <MoreVertIcon fontSize="small" />
                                </IconButton>
                              </Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                <MathRenderer content={row.question_text || ''} variant="inherit" />
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                                {topicLabel !== '—' && <Chip label={topicLabel} size="small" variant="outlined" sx={{ fontSize: '0.75rem' }} />}
                                {difficultyLabel && <Chip label={difficultyLabel} size="small" variant="outlined" color="secondary" sx={{ fontSize: '0.75rem' }} />}
                              </Box>
                              <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                <Tooltip title="Preview">
                                  <IconButton size="small" color="info" onClick={() => { setViewQuestion(row); setViewDialogOpen(true); }} aria-label="Preview">
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title={canEdit ? 'Edit' : (row.used_in_tests > 0 ? 'Used in tests' : 'Not owner')}>
                                  <span>
                                    <IconButton size="small" color="primary" onClick={() => handleOpenEdit(row)} disabled={!canEdit} aria-label="Edit">
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title={canDelete ? 'Delete' : 'Cannot delete'}>
                                  <span>
                                    <IconButton size="small" color="error" onClick={() => canDelete && handleDelete(row.id)} disabled={!canDelete} aria-label="Delete">
                                      <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Stack>
                            </Paper>
                          );
                        })}
                      </Stack>
                    )}

                    {/* Shared action menu for mobile cards */}
                    <Menu
                      anchorEl={actionMenuAnchor}
                      open={Boolean(actionMenuAnchor)}
                      onClose={() => { setActionMenuAnchor(null); setSelectedRow(null); }}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                      {selectedRow && (
                        <>
                          {role !== 'champion' && (
                            <MenuItem onClick={() => { handleOpenAIGenerate(selectedRow); setActionMenuAnchor(null); setSelectedRow(null); }}>
                              <AutoAwesomeIcon sx={{ mr: 1.5, fontSize: 20 }} />
                              Generate Similar (AI)
                            </MenuItem>
                          )}
                          {role === 'admin' && (
                            <MenuItem onClick={() => { handleOpenPreviewDialog(selectedRow); setActionMenuAnchor(null); setSelectedRow(null); }}>
                              <QuestionMarkIcon sx={{ mr: 1.5, fontSize: 20 }} />
                              Preview & Update Status
                            </MenuItem>
                          )}
                          <MenuItem
                            onClick={() => {
                              if (selectedRow.used_in_tests === 0 && getCreatorId(selectedRow) === String(user?.id)) {
                                handleDelete(selectedRow.id);
                              }
                              setActionMenuAnchor(null);
                              setSelectedRow(null);
                            }}
                            disabled={!(selectedRow.used_in_tests === 0 && getCreatorId(selectedRow) === String(user?.id))}
                          >
                            <DeleteOutlineIcon sx={{ mr: 1.5, fontSize: 20, color: 'error.main' }} />
                            <Typography color="error.main">Delete</Typography>
                          </MenuItem>
                        </>
                      )}
                    </Menu>
                  </>
                )}

                {activeTab === 1 && (
                  <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>}>
                    {analyticsLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress />
                      </Box>
                    ) : (
                      <QuestionAnalytics
                        questions={analyticsQuestions}
                        allTopics={allTopics}
                        curriculumAssociations={curriculumAssociations}
                        role={role}
                        selfOnly={selfOnly}
                        userId={user?.id}
                        filters={filters}
                      />
                    )}
                  </Suspense>
                )}
              </Paper>
            </Box>
          </Box>

      {/* Instructions Dialog */}
      <Dialog
        open={openInstr}
        onClose={() => setOpenInstr(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Upload Instructions
            </Typography>
            <IconButton onClick={() => setOpenInstr(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            To upload questions, prepare a CSV file with the following columns:
          </Typography>
          
          <Typography gutterBottom mt={2}>
            1. <b>Required columns</b>: question_text, board, academic_year, difficulty, question_type, options, correct_answer, subject
          </Typography>
          
          <Typography gutterBottom mt={2}>
            2. <b>Optional columns</b>: topic, exam_mode, tags, answer_evaluation
          </Typography>

          <Typography gutterBottom mt={2}>
            3. <b>Accepted values for board</b>:
          </Typography>
          <ul>
            {(boards||[]).map(b=> (<li key={b.id}><code>{b.name}</code></li>))}
          </ul>

          <Typography gutterBottom mt={2}>
            4. <b>Accepted values for academic_year</b>:
          </Typography>
          <ul>
            {(academicYears||[]).map(y=> (<li key={y.id}><code>{y.year}</code></li>))}
          </ul>

          <Typography gutterBottom mt={2}>
            5. <b>Accepted values for difficulty</b>:
          </Typography>
          <ul>
            {(difficulties||[]).map(d=> (<li key={d.id}><code>{d.name}</code></li>))}
          </ul>

          <Typography gutterBottom mt={2}>
            6. <b>Accepted values for exam_mode</b>:
          </Typography>
          <ul>
            {(examModes||[]).map(m=> (<li key={m.id}><code>{m.mode}</code></li>))}
          </ul>

          <Typography gutterBottom mt={2}>
            7. <b>Sequential combinations support</b>:
          </Typography>
          <ul>
            <li><b>board</b> – Use comma-separated values for multiple boards (e.g., <code>CBSE,IGCSE</code>)</li>
            <li><b>academic_year</b> – Use comma-separated values for multiple years (e.g., <code>6,7</code>)</li>
            <li><b>difficulty</b> – Use comma-separated values for multiple difficulties (e.g., <code>{(difficulties||[]).slice(0,2).map(d=>d.name).join(',')}</code>)</li>
            <li><b>Sequential pairing</b>: Values are paired by position (1st with 1st, 2nd with 2nd, etc.)</li>
            <li><b>Example</b>: <code>board='{(boards||[]).slice(0,2).map(b=>b.name).join(',')}', academic_year='{(academicYears||[]).slice(0,2).map(y=>y.year).join(',')}', difficulty='{(difficulties||[]).slice(0,2).map(d=>d.name).join(',')}'</code></li>
            <li><b>Result</b>: {(boards||[]).slice(0,2).map((b,i)=>`${b.name}-${(academicYears||[])[i]?.year || 'N'}-${(difficulties||[])[i]?.name || 'N'}`).join(', ')}</li>
            <li><b>Note</b>: If lists have different lengths, values cycle to match the longest list</li>
          </ul>

          <Typography gutterBottom mt={2}>
            8. <b>Column details & rules</b>:
          </Typography>
          <ul>
            <li><b>question_text</b> – any plain text. Avoid HTML tags.</li>
            <li><b>board</b> – one of the board names listed above or its ID (supports sequential multiple values).</li>
            <li><b>academic_year</b> – numeric year (e.g., 7, 10) or matching ID (supports sequential multiple values).</li>
            <li><b>difficulty</b> – {(difficulties||[]).map(d=>d.name).join(', ')} or their IDs (supports sequential multiple values).</li>
            <li><b>subject</b> – choose from the subject list above (case-insensitive) or its ID.</li>
            <li><b>topic</b> (optional) – must belong to the chosen subject; leave blank if none.</li>
            <li><b>question_type</b> – controls options & correct answer:
              <ul>
                <li><code>TRUE_FALSE</code> – <code>options</code> must be <code>True,False</code> or <code>True|False</code>; <code>correct_answer</code> is <code>True</code> or <code>False</code>.</li>
                <li><code>MULTIPLE</code> – <code>options</code> comma or pipe-separated with 2-6 choices (e.g., <code>A,B,C,D</code> or <code>A|B|C|D</code>); <code>correct_answer</code> must match one choice exactly.</li>
                <li><code>SHORT_ANSWER</code> – leave <code>options</code> blank; <code>correct_answer</code> is free text.</li>
              </ul>
            </li>
            <li><b>difficulty</b> – one of the accepted values listed above.</li>
            <li><b>options</b> – comma or pipe-separated values; required for MULTIPLE & TRUE_FALSE.</li>
            <li><b>exam_mode</b> (optional) – one of the modes listed above.</li>
            <li><b>correct_answer</b> – single value that matches the rules above.</li>
            <li><b>answer_evaluation</b> (optional) – JSON object for flexible short-answer marking, for example <code>{'{"mode":"numeric_with_unit","canonicalAnswer":{"value":14,"unit":"cm"},"unitRequired":true}'}</code>.</li>
          </ul>

          <Typography gutterBottom mt={2}>
            • Check for typos – uploader will reject unknown values.<br/>
            • If the preview highlights rows in <span style={{color:'red'}}>red</span>, hover over the cell to see the error. Fix the CSV and re-upload or edit directly in the preview grid before submitting.<br/>
            • After fixing, rows without errors will turn normal and the <b>Submit Valid Rows</b> button becomes enabled.
          </Typography>
        </DialogContent>
      </Dialog>

      {openUpload && (
        <QuestionUploadDialog
          open={openUpload}
          onClose={()=> setOpenUpload(false)}
          subjects={subjects}
          allTopics={allTopics}
          boards={boards}
          academicYears={academicYears}
          refreshQuestions={fetchQuestions}
          onSuccess={(msg)=>{ setSnackMessage(msg || 'All questions added successfully'); setSnackOpen(true); }}
          isChampions={role === 'champion'}
        />
      )}

      {/* Edit Question dialog */}
      <QuestionDialog
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        mode="edit"
        question={editQuestion}
        onSave={handleUpdateQuestion}
        onStateChange={setEditQuestion}
        subjects={subjects}
        allTopics={allTopics}
        boards={boards}
        academicYears={academicYears}
        curriculumAssociations={curriculumAssociations}
        difficulties={difficulties}
        examModes={examModes}
        onOpenDetailAnswer={handleOpenDetailAnswer}
        statuses={dialogStatuses}
        isChampions={role === 'champion'}
        detailAnswerRefreshTrigger={detailAnswerRefreshTrigger}
      />

      {/* Detail Answer Dialog */}
      <DetailAnswerDialog
        open={openDetailAnswer}
        onClose={() => setOpenDetailAnswer(false)}
        questionId={currentQuestionId}
        existingDetailAnswer={currentDetailAnswer}
        loadingDetailAnswer={detailAnswerLoading}
        onSave={handleDetailAnswerSave}
      />

      {/* Success Snackbar */}
      <Snackbar
        open={snackOpen}
        autoHideDuration={6000}
        onClose={() => setSnackOpen(false)}
        message={snackMessage}
      />

      {/* Question Preview Dialog */}
      <QuestionPreviewDialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        question={previewQuestion}
        onStatusUpdate={handleStatusUpdate}
      />

      {/* View Question Dialog */}
      <ViewQuestionDialog
        open={viewDialogOpen}
        onClose={() => {
          setViewDialogOpen(false);
          setViewQuestion(null);
        }}
        question={viewQuestion}
      />

      {/* AI Generate Dialog (similar questions from existing) */}
      <AIGenerateDialog
        open={aiGenerateOpen}
        onClose={() => setAiGenerateOpen(false)}
        sourceQuestion={aiSourceQuestion}
        difficulties={difficulties}
        onSuccess={(count) => {
          setSnackMessage(`${count} question${count > 1 ? 's' : ''} generated successfully!`);
          setSnackOpen(true);
          fetchQuestions(1);
          fetchTotalInDb();
        }}
        onError={(error) => {
          setSnackMessage(error || 'Failed to generate questions');
          setSnackOpen(true);
        }}
      />

    </>
  );
};

export default QuestionBank;
